<template>
  <div
    class="chat-atendimento d-flex flex-column h-100 position-relative"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- OVERLAY DE DRAG AND DROP (tela inteira) se estiver na janela de 24h bloquear envio -->
    <div
      v-if="isDragOver && !chatForaDaJanela"
      class="drag-overlay"
      @click="isDragOver = false; $bvToast.toast('Você não pode enviar arquivos na janela de 24h.', {
        title: 'Janela de 24h',
        variant: 'warning',
        solid: true,
      })"
    >
      <div class="drag-content">
        <i
          class="feather icon-upload-cloud"
          style="font-size: 3rem; color: #9A3092;"
        />
        <h5 class="mt-2 mb-1">
          Solte os arquivos aqui
        </h5>
        <p class="text-muted mb-0">
          Imagens, vídeos e PDFs são aceitos
        </p>
      </div>
    </div>

    <!-- Header -->
    <div class="chat-header p-2 d-flex justify-content-between align-items-center shadow-sm flex-wrap">
      <!-- Botão Voltar (apenas mobile) -->
      <b-button
        size="sm"
        variant="outline-primary"
        class="btn-voltar-mobile mr-2"
        @click="voltarParaLista"
      >
        <i class="feather icon-arrow-left" />
      </b-button>

      <!-- Lado esquerdo: Avatar + Nome + Etiquetas (clicável para abrir informações) -->
      <div
        v-if="cliente && !cliente.is_private_chat && !cliente.is_group"
        class="d-flex align-items-center flex-grow-1"
      >
        <!-- Avatar + Nome (clicável) -->
        <div
          id="tour-nome-cliente"
          class="d-flex align-items-center header-contato-info"
          style="cursor: pointer; background-color: #f7f9fb;"
          title="Clique para ver/ocultar informações do contato"
          @click.stop="abrirCardCliente"
        >
          <div class="position-relative mr-2">
            <b-avatar
              size="40px"
              :src="avatarUrl(getNomeCliente())"
            />
          </div>

          <div class="d-flex flex-column flex-grow-1">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <strong class="contato-nome">{{ getNomeCliente() }}</strong>
              <!-- Etiquetas do cliente como chips (versão desktop) -->
              <div
                v-if="cliente && cliente.tags && cliente.tags.length > 0"
                class="d-none d-md-flex align-items-center gap-1 tags-header-chips"
              >
                <span
                  v-for="(tag, index) in cliente.tags.slice(0, 3)"
                  :key="`tag-chip-${index}-${tag}`"
                  class="tag-chip-header"
                  :style="{
                    backgroundColor: getCorTag(tag) + '20',
                    borderColor: getCorTag(tag),
                    color: getCorTag(tag),
                    marginRight: '5px',
                  }"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="tag-chip-remove-header"
                    :title="`Remover etiqueta ${tag}`"
                    @click.stop="removerTag(tag)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="10"
                    />
                  </button>
                </span>
                <span
                  v-if="cliente.tags.length > 3"
                  class="tags-mais-header"
                >
                  +{{ cliente.tags.length - 3 }}
                </span>
              </div>
              <!-- Bolinhas das etiquetas (versão mobile) -->
              <div
                v-if="cliente && cliente.tags && cliente.tags.length > 0"
                class="d-flex d-md-none align-items-center gap-1 tag-dots-container"
              >
                <b-tooltip
                  v-for="(tag, index) in cliente.tags"
                  :key="`tag-dot-tooltip-${index}-${tag}`"
                  :target="`tag-dot-${cliente.id}-${index}`"
                  placement="top"
                  triggers="hover"
                >
                  {{ tag }}
                </b-tooltip>
                <span
                  v-for="(tag, index) in cliente.tags"
                  :id="`tag-dot-${cliente.id}-${index}`"
                  :key="`tag-dot-span-${index}-${tag}`"
                  class="tag-dot"
                  :style="{ backgroundColor: getCorTag(tag) }"
                />
              </div>
            </div>
            <p class="text-muted small mb-0">
              Clique para ver os dados do cliente
            </p>
          </div>
        </div>

      </div>

      <!-- Lado direito: Busca + Botão Etiquetas + Dropdown de Ações -->
      <div class="d-flex align-items-center gap-2">
        <!-- Botão de busca inline -->
        <button
          v-if="cliente && !cliente.is_group && !cliente.is_private_chat"
          class="btn btn-outline-primary btn-sm d-flex align-items-center"
          title="Buscar na conversa"
          @click="buscaInlineAtiva = !buscaInlineAtiva"
        >
          <feather-icon
            icon="SearchIcon"
            size="14"
          />
        </button>

        <!-- Botão para adicionar etiqueta -->
        <CampoTags
          v-if="cliente && !cliente.is_group && !cliente.is_private_chat"
          id="tour-botao-tags"
          ref="campoTags"
          v-model="cliente.tags"
          v-b-tooltip.hover.top="'Gerenciar etiquetas do cliente'"
          class="campo-tags-header"
          :tags-disponiveis="tagsDisponiveis"
          @input="$emit('atualizar-tags', $event)"
          @dropdown-opened="fecharDropdown"
        />

        <!-- Dropdown de Ações -->
        <div
          v-if="cliente && !cliente.is_group && !cliente.is_private_chat"
          id="tour-dropdown-acoes"
          v-click-outside="fecharDropdown"
          class="dropdown-acoes-container"
        >
          <button
            class="btn btn-outline-primary btn-sm d-flex align-items-center"
            type="button"
            @click="toggleDropdown"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="14"
              class="mr-1"
            /> Ações
            <feather-icon
              icon="ChevronDownIcon"
              size="14"
              class="ml-1"
              :class="{ 'rotated': dropdownAberto }"
            />
          </button>

          <div
            class="dropdown-menu dropdown-menu-right dropdown-menu-acoes"
            :class="{ show: dropdownAberto }"
          >
            <!-- Pedidos -->
            <a
              class="dropdown-item"
              href="#"
              @click.prevent="executarAcao('pedidos')"
            >
              <i class="feather icon-package mr-2 text-info" />
              Ver Pedidos
            </a>

            <!-- Resolver -->
            <a
              class="dropdown-item"
              href="#"
              :class="{ disabled: !chatForaDaJanela && (atendimentoResolvido || cliente.atendimento_bot) }"
              @click.prevent="executarAcao('resolver')"
            >
              <i
                class="feather icon-check mr-2"
                :class="chatForaDaJanela ? 'text-danger' : 'text-success'"
              />
              {{ chatForaDaJanela ? 'Resolver' : 'Resolver' }}
            </a>

            <!-- Interromper Bot -->
            <a
              v-if="cliente.atendimento_bot"
              class="dropdown-item"
              href="#"
              @click.prevent="executarAcao('interromper')"
            >
              <i class="feather icon-x-circle mr-2 text-danger" />
              Interromper IA
            </a>

            <div class="dropdown-divider" />

            <!-- Header -->
            <h6 class="dropdown-header">
              Outras opções
            </h6>

            <!-- Template - NÃO mostrar para chats privados entre atendentes -->
            <a
              v-if="!cliente.is_private_chat"
              id="tour-enviar-template"
              class="dropdown-item"
              href="#"
              @click.prevent="executarAcao('template')"
            >
              <i class="feather icon-message-square mr-2 text-primary" />
              Enviar Template
            </a>
          </div>
        </div>
      </div>

    </div>

    <!-- Barra de busca inline -->
    <div
      v-if="buscaInlineAtiva"
      class="search-inline-bar"
      @keydown.esc="fecharBuscaInline"
    >
      <feather-icon
        icon="SearchIcon"
        size="16"
        class="text-muted"
      />
      <input
        ref="inputBuscaInline"
        v-model="termoBuscaInline"
        type="text"
        placeholder="Buscar na conversa..."
        @keydown.enter.prevent="navegarBuscaProximo"
        @keydown.esc="fecharBuscaInline"
      >
      <span
        v-if="termoBuscaInline.length >= 3"
        class="search-result-counter"
      >
        {{ resultadosBusca.length === 0 ? 'Nenhum resultado' : `${indiceResultadoAtual + 1} de ${resultadosBusca.length}` }}
      </span>
      <button
        v-if="termoBuscaInline.length >= 3 && resultadosBusca.length > 0"
        class="btn btn-sm btn-link p-0"
        title="Resultado anterior"
        @click="navegarBuscaAnterior"
      >
        <feather-icon
          icon="ChevronUpIcon"
          size="16"
        />
      </button>
      <button
        v-if="termoBuscaInline.length >= 3 && resultadosBusca.length > 0"
        class="btn btn-sm btn-link p-0"
        title="Próximo resultado"
        @click="navegarBuscaProximo"
      >
        <feather-icon
          icon="ChevronDownIcon"
          size="16"
        />
      </button>
      <button
        class="btn btn-sm btn-link p-0 text-muted"
        title="Fechar busca"
        @click="fecharBuscaInline"
      >
        <feather-icon
          icon="XIcon"
          size="16"
        />
      </button>
    </div>

    <!-- Corpo do Chat -->
    <div
      ref="chatBody"
      class="chat-body flex-grow-1 overflow-auto p-2"
      :class="{ 'chat-body-with-alert': chatForaDaJanela && !cliente.is_group && !cliente.is_private_chat }"
    >
      <!-- Sessões anteriores + contexto da citação (histórico por waId), acima da conversa atual -->
      <div
        v-if="mostrarHistoricoConversasTopo"
        class="chat-historico-conversas"
      >
        <div class="chat-historico-conversas__heading">
          Histórico de conversas
        </div>
        <template v-for="(item, histIdx) in historicoTopoItens">
          <div
            v-if="item.type === 'date'"
            :key="'hist-date-' + histIdx"
            class="chat-historico-date-row"
          >
            <span class="chat-historico-date-row__label">{{ item.label }}</span>
          </div>
          <div
            v-else
            :key="'hist-card-' + histIdx"
            class="chat-historico-card"
            role="button"
            tabindex="0"
            @click="abrirHistoricoAtendimentoDoTopo(item.atendimento_id)"
            @keydown.enter.prevent="abrirHistoricoAtendimentoDoTopo(item.atendimento_id)"
          >
            <div class="chat-historico-card__body">
              <div class="chat-historico-card__status">
                Conversa encerrada
              </div>
              <div class="chat-historico-card__preview text-truncate">
                {{ item.preview }}
              </div>
            </div>
            <div class="chat-historico-card__time">
              {{ item.hora }}
            </div>
          </div>
        </template>
        <div class="chat-historico-divider-atual">
          <span>Início da conversa atual</span>
        </div>
      </div>

      <!-- Indicador de mensagens anteriores não carregadas -->
      <div
        v-if="mensagensOrdenadas.length > mensagensVisiveis.length"
        class="d-flex justify-content-center align-items-center py-2"
      >
        <b-spinner
          v-if="carregandoMaisMensagens"
          small
          variant="secondary"
        />
        <span
          v-else
          class="text-muted small"
        >↑ Role para carregar mais mensagens ({{ mensagensOrdenadas.length - mensagensVisiveis.length }} acima)</span>
      </div>

      <transition-group
        :name="shouldAnimateBubbles ? 'fade-bubble' : ''"
        tag="div"
      >
        <template v-for="(msg, index) in mensagensVisiveis">
          <!-- Marcador de troca de atendente -->
          <SeparadorTrocaAtendente
            v-if="msg.tipo === 'troca_atendente'"
            :key="msg.mensagemId || `troca-${index}`"
            :atendente-anterior="msg.atendente_anterior"
            :atendente-novo="msg.atendente_novo"
            :timestamp="msg.timestamp"
          />

          <!-- Mensagem normal -->
          <div
            v-else
            :key="msg.id || msg.timestamp || index"
            :ref="'msg-' + (msg.id || index)"
            :data-whatsapp-id="msg.whatsapp_id"
            class="mb-2 d-flex mensagem-item"
            :class="[
              deveAlinharDireita(msg) ? 'justify-content-end' : 'justify-content-start',
              { 'search-result-active': isMensagemResultadoAtual(msg) }
            ]"
            @dblclick="prepararResposta(msg)"
          >
            <div
              class="d-flex"
              :class="deveAlinharDireita(msg) ? 'flex-row-reverse' : ''"
            >
              <!-- <img v-if="msg.remetente === 'atendente'" src="https://i.pravatar.cc/150?img=13" class="msg-avatar ml-2" width="32" height="32" /> -->
              <div :class="['chat-bubble', `bubble-${getTipoBubble(msg)}`, { 'bubble-audio': msg.tipo === 'audio', 'bubble-sticker': msg.tipo === 'sticker' }]">
                <!-- Mensagem Referenciada (Quoted Message) -->
                <div
                  v-if="msg.referencia_mensagem_id"
                  class="quoted-message mb-2 rounded"
                  :class="msg.remetente === 'atendente' ? 'quoted-right' : 'quoted-left'"
                  @click="scrollParaMensagemReferenciada(msg)"
                >
                  <!-- Layout estilo WhatsApp: texto à esquerda, thumbnail à direita -->
                  <div class="quoted-inner">
                    <div class="quoted-body">
                      <div class="quoted-sender">
                        <template v-if="msg.mensagem_referenciada">
                          {{ msg.mensagem_referenciada.remetente === 'atendente' ? 'Você' : ((cliente.cliente && cliente.cliente.name) ? cliente.cliente.name : cliente.nome) }}
                        </template>
                        <template v-else-if="isIdOutgoing(msg.referencia_mensagem_id)">
                          Você
                        </template>
                        <template v-else>
                          {{ (cliente.cliente && cliente.cliente.name) ? cliente.cliente.name : cliente.nome }}
                        </template>
                      </div>

                      <template v-if="msg.mensagem_referenciada">
                        <!-- Imagem: ícone SVG + legenda -->
                        <template v-if="msg.mensagem_referenciada.tipo === 'image'">
                          <div class="quoted-media-label">
                            <svg
                              viewBox="0 0 24 24"
                              height="16"
                              width="16"
                              fill="currentColor"
                              class="quoted-media-icon"
                            >
                              <path d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V5H5V19ZM7 17H17C17.2 17 17.35 16.9083 17.45 16.725C17.55 16.5417 17.5333 16.3667 17.4 16.2L14.65 12.525C14.55 12.3917 14.4167 12.325 14.25 12.325C14.0833 12.325 13.95 12.3917 13.85 12.525L11.25 16L9.4 13.525C9.3 13.3917 9.16667 13.325 9 13.325C8.83333 13.325 8.7 13.3917 8.6 13.525L6.6 16.2C6.46667 16.3667 6.45 16.5417 6.55 16.725C6.65 16.9083 6.8 17 7 17Z" />
                            </svg>
                            <span
                              v-if="msg.mensagem_referenciada.texto"
                              class="quoted-media-caption"
                            >{{ msg.mensagem_referenciada.texto }}</span>
                            <span
                              v-else
                              class="quoted-media-caption quoted-media-caption--placeholder"
                            >Imagem</span>
                          </div>
                        </template>
                        <!-- Vídeo: ícone SVG + legenda -->
                        <template v-else-if="msg.mensagem_referenciada.tipo === 'video'">
                          <div class="quoted-media-label">
                            <svg
                              viewBox="0 0 24 24"
                              height="16"
                              width="16"
                              fill="currentColor"
                              class="quoted-media-icon"
                            >
                              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                            </svg>
                            <span
                              v-if="msg.mensagem_referenciada.texto"
                              class="quoted-media-caption"
                            >{{ msg.mensagem_referenciada.texto }}</span>
                            <span
                              v-else
                              class="quoted-media-caption quoted-media-caption--placeholder"
                            >Vídeo</span>
                          </div>
                        </template>
                        <!-- Áudio -->
                        <template v-else-if="msg.mensagem_referenciada.tipo === 'audio'">
                          <div class="quoted-media-label">
                            <svg
                              viewBox="0 0 24 24"
                              height="16"
                              width="16"
                              fill="currentColor"
                              class="quoted-media-icon"
                            >
                              <path d="M12 3a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V7a4 4 0 0 1 4-4zm0 14.5c3.31 0 6-2.69 6-6H20c0 3.86-2.93 7.02-6.75 7.44V21h-2.5v-2.06C7.93 18.52 5 15.36 5 11.5H6.5c0 3.31 2.69 6 5.5 6z" />
                            </svg>
                            <span class="quoted-media-caption quoted-media-caption--placeholder">Áudio</span>
                          </div>
                        </template>
                        <!-- Arquivo genérico (ex.: tipos não mapeados como document) -->
                        <template v-else-if="msg.mensagem_referenciada.tipo === 'arquivo'">
                          <div class="quoted-media-label">
                            <svg
                              viewBox="0 0 24 24"
                              height="16"
                              width="16"
                              fill="currentColor"
                              class="quoted-media-icon"
                            >
                              <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                            </svg>
                            <span
                              v-if="msg.mensagem_referenciada.texto"
                              class="quoted-media-caption"
                            >{{ msg.mensagem_referenciada.texto }}</span>
                            <span
                              v-else
                              class="quoted-media-caption quoted-media-caption--placeholder"
                            >Respondeu a um arquivo</span>
                          </div>
                        </template>
                        <!-- Documento -->
                        <template v-else-if="msg.mensagem_referenciada.tipo === 'document' || msg.mensagem_referenciada.tipo === 'pdf'">
                          <div class="quoted-media-label">
                            <svg
                              viewBox="0 0 24 24"
                              height="16"
                              width="16"
                              fill="currentColor"
                              class="quoted-media-icon"
                            >
                              <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                            </svg>
                            <span class="quoted-media-caption quoted-media-caption--placeholder">Documento</span>
                          </div>
                        </template>
                        <!-- Texto puro -->
                        <template v-else>
                          <span class="quoted-text-clamp">
                            {{ msg.mensagem_referenciada.texto || msg.mensagem_referenciada.conteudo || 'Mensagem' }}
                          </span>
                        </template>
                      </template>

                      <template v-else-if="isIdOutgoing(msg.referencia_mensagem_id)">
                        <span class="quoted-text-clamp quoted-media-caption--placeholder">Respondeu a uma mensagem sua</span>
                      </template>
                      <template v-else>
                        <span class="quoted-text-clamp quoted-media-caption--placeholder">Mensagem original não disponível</span>
                      </template>
                    </div>

                    <!-- Thumbnail à direita (imagem ou vídeo), estilo WhatsApp -->
                    <!-- Imagem: usa background-image -->
                    <div
                      v-if="msg.mensagem_referenciada && msg.mensagem_referenciada.tipo === 'image' && msg.mensagem_referenciada.url"
                      class="quoted-thumbnail-right"
                      :style="{ backgroundImage: `url('${msg.mensagem_referenciada.url}')` }"
                    />
                    <!-- Vídeo: usa elemento <video> com preload para gerar thumbnail -->
                    <div
                      v-else-if="msg.mensagem_referenciada && msg.mensagem_referenciada.tipo === 'video' && msg.mensagem_referenciada.url"
                      class="quoted-thumbnail-right quoted-thumbnail-video"
                    >
                      <video
                        :src="msg.mensagem_referenciada.url"
                        muted
                        preload="metadata"
                        class="quoted-video-thumb"
                      />
                      <div class="quoted-video-play">
                        <svg
                          viewBox="0 0 24 24"
                          height="18"
                          width="18"
                          fill="white"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Nome do atendente em chat de grupo ou privado -->
                <div
                  v-if="(cliente.is_group || cliente.is_private_chat) && msg.sender_id && !isMinhaMensagem(msg.sender_id)"
                  class="atendente-meta text-muted small mb-1"
                  :class="msg.remetente === 'atendente' ? 'text-right' : 'text-left'"
                >
                  <strong>{{ getNomeAtendente(msg.sender_id) }}</strong>
                </div>

                <!-- Bot label inside bubble -->
                <div
                  v-if="msg.remetente === 'bot'"
                  class="bot-meta-inline"
                >
                  🤖 Assistente
                </div>

                <div class="conteudo-msg message-text">
                  <!-- Audio -->
                  <template v-if="msg.tipo === 'audio' && msg.url">
                    <div class="audio-player-ui">
                      <!-- Botão play/pause circular -->
                      <button
                        class="btn-audio-play"
                        :aria-label="audioPlayingId === msgKey(msg) ? 'Pausar' : 'Reproduzir'"
                        @click="togglePlayAudio(msg)"
                      >
                        <svg
                          v-if="audioPlayingId !== msgKey(msg)"
                          viewBox="0 0 24 24"
                          height="20"
                          width="20"
                          fill="currentColor"
                        >
                          <path d="M9.525 18.025C9.19167 18.2417 8.85417 18.2542 8.5125 18.0625C8.17083 17.8708 8 17.575 8 17.175V6.825C8 6.425 8.17083 6.12917 8.5125 5.9375C8.85417 5.74583 9.19167 5.75833 9.525 5.975L17.675 11.15C17.975 11.35 18.125 11.6333 18.125 12C18.125 12.3667 17.975 12.65 17.675 12.85L9.525 18.025Z" />
                        </svg>
                        <svg
                          v-else
                          viewBox="0 0 24 24"
                          height="20"
                          width="20"
                          fill="currentColor"
                        >
                          <path d="M9 19c-.55 0-1.02-.196-1.412-.587A1.927 1.927 0 0 1 7 17V7c0-.55.196-1.02.588-1.413A1.925 1.925 0 0 1 9 5c.55 0 1.021.196 1.413.587C10.804 5.98 11 6.45 11 7v10c0 .55-.196 1.021-.587 1.413A1.928 1.928 0 0 1 9 19Zm6 0c-.55 0-1.021-.196-1.413-.587A1.928 1.928 0 0 1 13 17V7c0-.55.196-1.02.587-1.413A1.928 1.928 0 0 1 15 5c.55 0 1.021.196 1.413.587C16.804 5.98 17 6.45 17 7v10c0 .55-.196 1.021-.587 1.413A1.928 1.928 0 0 1 15 19Z" />
                        </svg>
                      </button>

                      <!-- Coluna central: waveform + linha de info -->
                      <div class="audio-center">
                        <div
                          class="audio-waveform"
                          @click="seekAudioWaveform(msg, $event)"
                        >
                          <div
                            v-for="(bar, i) in (audioUi(msg)._waveformData || [])"
                            :key="i"
                            class="waveform-bar"
                            :class="{ 'waveform-bar--played': audioPlayingId === msgKey(msg) && i < (audioUi(msg)._waveformData || []).length * (audioProgress || 0) / 100 }"
                            :style="{ height: Math.max(3, bar * 22) + 'px' }"
                          />
                          <template v-if="!audioUi(msg)._waveformData">
                            <div
                              v-for="n in 40"
                              :key="'ph-'+n"
                              class="waveform-bar"
                              style="height: 6px;"
                            />
                          </template>
                        </div>
                        <div class="audio-info-row">
                          <span class="audio-time">{{ formatTime(audioPlayingId === msgKey(msg) ? audioCurrentTime : (audioUi(msg)._duration || 0)) }}</span>
                          <button
                            class="btn-audio-speed"
                            @click="alternarVelocidadeAudio"
                          >
                            {{ velocidadeAudio.toFixed(1) }}×
                          </button>
                        </div>
                      </div>

                      <!-- Avatar com ícone de microfone -->
                      <div class="audio-avatar-wrap">
                        <div class="audio-avatar">
                          <svg
                            viewBox="0 0 48 48"
                            height="38"
                            width="38"
                            fill="none"
                          >
                            <path
                              d="M24 23q-1.857 0-3.178-1.322Q19.5 20.357 19.5 18.5t1.322-3.178T24 14t3.178 1.322Q28.5 16.643 28.5 18.5t-1.322 3.178T24 23m-6.75 10q-.928 0-1.59-.66-.66-.662-.66-1.59v-.9q0-.956.492-1.758A3.3 3.3 0 0 1 16.8 26.87a16.7 16.7 0 0 1 3.544-1.308q1.8-.435 3.656-.436 1.856 0 3.656.436T31.2 26.87q.816.422 1.308 1.223T33 29.85v.9q0 .928-.66 1.59-.662.66-1.59.66z"
                              fill="rgba(255,255,255,0.6)"
                            />
                          </svg>
                        </div>
                        <span class="audio-mic-badge">
                          <svg
                            viewBox="0 0 19 26"
                            height="24"
                            width="24"
                            fill="currentColor"
                          >
                            <path d="M9.367,15.668c1.527,0,2.765-1.238,2.765-2.765V5.265c0-1.527-1.238-2.765-2.765-2.765S6.603,3.738,6.603,5.265v7.638C6.603,14.43,7.84,15.668,9.367,15.668z M14.655,12.91h-0.3c-0.33,0-0.614,0.269-0.631,0.598c0,0,0,0-0.059,0.285c-0.41,1.997-2.182,3.505-4.298,3.505c-2.126,0-3.904-1.521-4.304-3.531C5.008,13.49,5.008,13.49,5.008,13.49c-0.016-0.319-0.299-0.579-0.629-0.579h-0.3c-0.33,0-0.591,0.258-0.579,0.573c0,0,0,0,0.04,0.278c0.378,2.599,2.464,4.643,5.076,4.978v3.562c0,0.33,0.27,0.6,0.6,0.6h0.3c0.33,0,0.6-0.27,0.6-0.6V18.73c2.557-0.33,4.613-2.286,5.051-4.809c0.057-0.328,0.061-0.411,0.061-0.411C15.243,13.18,14.985,12.91,14.655,12.91z" />
                          </svg>
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="!audioUi(msg)._transcricao"
                      class="transcrever-link mt-1"
                      @click="transcreverAudio(msg)"
                    >
                      <span
                        v-if="!audioUi(msg)._transcrevendo"
                        class="text-primary font-italic transcrever-text"
                      >Transcrever</span>
                      <span
                        v-else
                        class="text-muted font-italic"
                      >Transcrevendo...</span>
                    </div>
                    <div
                      v-if="audioUi(msg)._transcricao && audioUi(msg)._transcricao.length > 0"
                      ref="transcricaoContent"
                      class="transcricao-audio mt-1"
                      :class="{ 'expandida': audioUi(msg)._transcricaoExpandida }"
                    >
                      {{ `"` + audioUi(msg)._transcricao + `"` }}
                    </div>
                    <div
                      v-if="audioUi(msg)._transcricao && audioUi(msg)._transcricaoComprida"
                      class="toggle-transcricao mt-1 text-primary font-italic"
                      @click="toggleTranscricaoExpandida(msg)"
                    >
                      {{ audioUi(msg)._transcricaoExpandida ? 'Recolher' : 'Ver mais' }}
                    </div>
                  </template>

                  <!-- Image -->
                  <template v-if="msg.tipo === 'image'">
                    <div class="img-bubble-wrapper">
                      <div
                        class="img-bubble-container"
                        @click="abrirMidia(msg)"
                      >
                        <img
                          :src="msg.url"
                          alt="imagem"
                          class="img-msg-preview img-fluid"
                        >
                        <!-- Timestamp sobre a imagem quando não há legenda -->
                        <span
                          v-if="!msg.texto"
                          class="img-timestamp-overlay"
                        >
                          <span class="img-timestamp-content">
                            {{ formatHoraData(msg.timestamp) }}
                            <span
                              v-if="msg.remetente === 'atendente'"
                              class="msg-status-icon"
                              v-html="getStatusIcon(msg.status)"
                            />
                          </span>
                        </span>
                      </div>
                      <div
                        v-if="msg.texto"
                        class="img-caption"
                      >
                        <span
                          v-if="buscaInlineAtiva && termoBuscaInline.length >= 3"
                          v-html="highlightText(msg.texto, termoBuscaInline)"
                        />
                        <template v-else>
                          {{ msg.texto }}
                        </template>
                        <!-- Timestamp inline junto à legenda -->
                        <span class="msg-timestamp-inline">
                          <span
                            class="msg-timestamp-spacer"
                            aria-hidden="true"
                          />
                          <span class="msg-timestamp-content">
                            {{ formatHoraData(msg.timestamp) }}
                            <span
                              v-if="msg.remetente === 'atendente'"
                              class="msg-status-icon"
                              v-html="getStatusIcon(msg.status)"
                            />
                          </span>
                        </span>
                      </div>
                    </div>
                  </template>

                  <!-- Sticker -->
                  <template v-else-if="msg.tipo === 'sticker'">
                    <img
                      :src="msg.url"
                      alt="sticker"
                      class="sticker-img img-fluid"
                    >
                  </template>

                  <!-- Video (legenda/caption como na imagem — msg.texto / msg.conteudo) -->
                  <template v-else-if="msg.tipo === 'video'">
                    <div class="img-bubble-wrapper">
                      <div
                        class="video-bubble-container"
                        @click="abrirMidia(msg)"
                      >
                        <video
                          :src="msg.url"
                          muted
                          preload="metadata"
                          class="video-bubble-thumb"
                        />
                        <div class="video-play-overlay">
                          <div class="video-play-circle">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="white"
                            ><polygon points="5,3 19,12 5,21" /></svg>
                          </div>
                        </div>
                        <span
                          v-if="!(msg.texto || msg.conteudo)"
                          class="img-timestamp-overlay"
                        >
                          <span class="img-timestamp-content">
                            {{ formatHoraData(msg.timestamp) }}
                            <span
                              v-if="msg.remetente === 'atendente'"
                              class="msg-status-icon"
                              v-html="getStatusIcon(msg.status)"
                            />
                          </span>
                        </span>
                      </div>
                      <div
                        v-if="msg.texto || msg.conteudo"
                        class="img-caption"
                      >
                        <span
                          v-if="buscaInlineAtiva && termoBuscaInline.length >= 3"
                          v-html="highlightText(msg.texto || msg.conteudo, termoBuscaInline)"
                        />
                        <template v-else>
                          {{ msg.texto || msg.conteudo }}
                        </template>
                        <span class="msg-timestamp-inline">
                          <span
                            class="msg-timestamp-spacer"
                            aria-hidden="true"
                          />
                          <span class="msg-timestamp-content">
                            {{ formatHoraData(msg.timestamp) }}
                            <span
                              v-if="msg.remetente === 'atendente'"
                              class="msg-status-icon"
                              v-html="getStatusIcon(msg.status)"
                            />
                          </span>
                        </span>
                      </div>
                    </div>
                  </template>

                  <!-- Document / PDF / arquivo genérico: download direto (sem modal de preview) -->
                  <template v-else-if="msg.tipo === 'document' || msg.tipo === 'pdf' || msg.tipo === 'arquivo'">
                    <div
                      class="doc-card"
                      title="Baixar arquivo"
                      role="button"
                      tabindex="0"
                      @click="baixarArquivoMensagem(msg)"
                      @dblclick.stop="prepararResposta(msg)"
                      @keydown.enter.prevent="baixarArquivoMensagem(msg)"
                      @keydown.space.prevent="baixarArquivoMensagem(msg)"
                    >
                      <div
                        class="doc-card-icon"
                        :class="'doc-icon--' + getDocExtension(msg)"
                      >
                        <svg
                          viewBox="0 0 22 26"
                          width="34"
                          height="40"
                          fill="none"
                        >
                          <path
                            fill="currentColor"
                            d="M1 5.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C3.28 1 4.12 1 5.8 1h5.549c.978 0 1.468 0 1.928.11.408.099.798.26 1.156.48.404.247.75.593 1.441 1.285L17 4l2.125 2.125c.692.692 1.038 1.038 1.286 1.442a4 4 0 0 1 .479 1.156c.11.46.11.95.11 1.928V20.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 25 17.88 25 16.2 25H5.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C1 22.72 1 21.88 1 20.2V5.8Z"
                          />
                          <path
                            fill="rgba(255,255,255,0.4)"
                            fill-rule="evenodd"
                            d="m20.71 8.092-5.975-.026a1 1 0 0 1-.995-1.004l.025-5.81c.219.086.43.19.631.314.4.244.744.586 1.43 1.268L17 3.999l2.143 2.13c.701.696 1.052 1.044 1.302 1.452.101.164.19.335.266.511Z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <span class="doc-icon-badge">{{ getDocExtension(msg).toUpperCase() }}</span>
                      </div>
                      <div class="doc-card-info">
                        <span
                          class="doc-card-name"
                          :title="msg.filename || msg.url"
                        >{{ getDocFilename(msg) }}</span>
                        <span class="doc-card-meta">
                          <span>{{ getDocExtension(msg).toUpperCase() }}</span>
                          <span
                            v-if="msg.tamanho"
                            class="doc-meta-separator"
                          >•</span>
                          <span v-if="msg.tamanho">{{ getFileSize(msg.tamanho) }}</span>
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="msg.texto"
                      class="doc-caption"
                    >
                      {{ msg.texto }}
                    </div>
                  </template>

                  <!-- Text -->
                  <template v-else>
                    <span
                      v-if="buscaInlineAtiva && termoBuscaInline.length >= 3"
                      v-html="highlightText(msg.texto, termoBuscaInline)"
                    />
                    <span
                      v-else
                      :class="{ 'only-emoji': isOnlyEmojis(msg.texto) }"
                    >{{ msg.texto }}</span>
                  </template>

                  <!-- Inline timestamp + status (inside bubble) -->
                  <span
                    v-if="msg.tipo !== 'sticker' && msg.tipo !== 'image' && msg.tipo !== 'video'"
                    class="msg-timestamp-inline"
                  >
                    <span
                      class="msg-timestamp-spacer"
                      aria-hidden="true"
                    />
                    <span class="msg-timestamp-content">
                      {{ formatHoraData(msg.timestamp) }}
                      <span
                        v-if="msg.remetente === 'atendente'"
                        class="msg-status-icon"
                        v-html="getStatusIcon(msg.status)"
                      />
                    </span>
                  </span>
                </div>

                <!-- Progress bar for uploads -->
                <b-progress
                  v-if="msg.status === 'enviando'"
                  :value="msg.progresso"
                  :max="100"
                  height="6px"
                  class="mt-2"
                  variant="info"
                />

                <!-- Badge de reação -->
                <div
                  v-if="reacaoDe(msg) && reacaoDe(msg).emoji"
                  class="reaction-badge"
                  :class="{ 'reaction-badge--outbound': reacaoDe(msg).outbound }"
                  :title="reacaoDe(msg).outbound ? 'Clique para remover reação' : 'Reação do cliente'"
                  @click="reacaoDe(msg).outbound ? removerReacao(msg) : null"
                >{{ reacaoDe(msg).emoji }}</div>
              </div>

              <!-- Floating action buttons (reactions + reply) -->
              <div
                v-click-outside="() => closeReactions(msg)"
                class="reactions-wrapper"
                :class="deveAlinharDireita(msg) ? 'reactions-left' : 'reactions-right'"
              >
                <button
                  v-if="msg.remetente === 'cliente'"
                  class="btn-reaction"
                  :disabled="!msg.whatsapp_id"
                  title="Reagir"
                  @click="toggleReactions(msg)"
                >
                  🙂
                </button>
                <div
                  v-if="msg.remetente === 'cliente' && audioUi(msg)._showReactions"
                  class="reactions-picker"
                >
                  <span
                    v-for="emoji in reactionEmojis"
                    :key="emoji"
                    class="emoji-btn"
                    @click="reagir(msg, emoji)"
                  >{{ emoji }}</span>
                </div>
                <button
                  class="btn-reaction btn-reply"
                  title="Responder"
                  @click="prepararResposta(msg)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
                </button>
              </div>
            </div>
          </div>
        </template>
      </transition-group>

      <transition name="fade-typing">
        <div
          v-if="clienteDigitando"
          class="typing-indicator"
        >
          <div class="typing-bubble">
            <span class="typing-dot" />
            <span class="typing-dot" />
            <span class="typing-dot" />
          </div>
        </div>
      </transition>
    </div>

    <!-- Botão Scroll to Bottom - Estilo WhatsApp -->
    <div
      v-if="showScrollButton"
      class="scroll-to-bottom-container"
    >
      <span class="">
        <button
          class="scroll-to-bottom-btn"
          tabindex="0"
          data-tab="7"
          aria-disabled="false"
          type="button"
          aria-label="Deslizar para o fim da página"
          :style="{ transform: 'scaleX(1) scaleY(1)', opacity: 1 }"
          @click="scrollToBottom"
        >
          <span
            aria-hidden="true"
            data-icon="ic-chevron-down-wide"
            class="scroll-icon"
          >
            <svg
              viewBox="0 0 24 24"
              height="24"
              width="24"
              preserveAspectRatio="xMidYMid meet"
              class=""
              fill="none"
            >
              <title>ic-chevron-down-wide</title>
              <path
                d="M11 13.6L6.11253 8.71253C5.72003 8.32003 5.08281 8.32285 4.69381 8.7188C4.30964 9.10983 4.31241 9.73741 4.70003 10.125L11.2669 16.6919C11.6718 17.0968 12.3282 17.0968 12.7331 16.6919L19.3 10.125C19.6876 9.73741 19.6904 9.10983 19.3062 8.7188C18.9172 8.32285 18.28 8.32003 17.8875 8.71253L13 13.6L12 14.625L11 13.6Z"
                fill="currentColor"
              />
            </svg>
          </span>
        </button>
      </span>
    </div>

    <!-- Rodapé -->

    <!-- T20: Bot overlay — shown ABOVE the footer when bot is active -->
    <transition name="fade-overlay">
      <div
        v-if="cliente.atendimento_bot === true"
        class="bot-overlay-banner"
      >
        <span>🤖 Atendimento automatizado</span>
      </div>
    </transition>

    <div class="chat-footer">
      <!-- T20: 24h warning banner -->
      <transition name="fade-overlay">
        <div
          v-if="chatForaDaJanela && !cliente.is_group && !cliente.is_private_chat"
          class="alert-24h-banner"
        >
          <div class="alert-24h-content">
            <feather-icon
              icon="AlertTriangleIcon"
              size="16"
              class="alert-24h-icon"
            />
            <span class="alert-24h-text">A janela de 24h expirou.</span>
            <button
              class="alert-24h-btn"
              @click="abrirModalTemplate"
            >
              Enviar Template
            </button>
          </div>
        </div>
      </transition>

      <!-- Audio recording bar -->
      <transition name="slide-up">
        <div
          v-if="gravandoAudio || previewAudioUrl"
          class="chat-input-row"
        >
          <!-- Recording state -->
          <div
            v-if="gravandoAudio"
            class="audio-recording-bar d-flex align-items-center w-100"
          >
            <button
              class="btn-recording-action btn-cancel"
              title="Cancelar"
              @click="cancelarGravacao"
            >
              <i class="feather icon-trash-2" />
            </button>

            <div class="waveform-container">
              <div
                v-for="(height, i) in waveformBars"
                :key="'bar-' + i"
                class="waveform-bar"
                :style="{ height: height + 'px' }"
              />
            </div>

            <div class="recording-timer">
              {{ formatTime(cronometro) }}
            </div>

            <button
              class="btn-recording-action btn-stop"
              title="Parar e enviar"
              @click="toggleGravacao"
            >
              <i class="feather icon-check" />
            </button>
          </div>

          <!-- Preview state -->
          <div
            v-else
            class="audio-recording-bar d-flex align-items-center w-100"
          >
            <button
              class="btn-recording-action btn-cancel"
              title="Descartar"
              @click="cancelarGravacao"
            >
              <i class="feather icon-trash-2" />
            </button>

            <div class="flex-grow-1 mx-2">
              <b-progress
                :value="progressBarValue"
                height="4px"
                variant="success"
              />
            </div>

            <button
              class="btn-recording-action"
              style="background: #f0f0f0; color: #333;"
              @click="togglePlayPreview"
            >
              <i :class="isPreviewPlaying ? 'feather icon-pause' : 'feather icon-play'" />
            </button>

            <div class="recording-timer">
              {{ formatTime(previewAudioCurrentTime) }}
            </div>

            <button
              class="btn-recording-action btn-stop"
              :disabled="enviandoAudio || audioStatus === 'enviando'"
              title="Enviar áudio"
              @click="enviarAudioGravado"
            >
              <b-spinner
                v-if="enviandoAudio"
                small
              />
              <i
                v-else
                class="feather icon-send"
              />
            </button>
          </div>
        </div>
      </transition>

      <!-- T15: WhatsApp-style input row -->
      <template v-if="!gravandoAudio && !previewAudioUrl">
        <div class="chat-input-row">
          <!-- Hidden file inputs -->
          <input
            ref="inputArquivo"
            type="file"
            multiple
            accept="image/jpeg,image/png,video/mp4,video/3gpp,application/pdf,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            style="display: none"
            @change="enviarAnexos"
          >
          <input
            ref="inputArquivoChatInterno"
            type="file"
            accept="image/*,video/*,audio/*,application/pdf,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            style="display: none"
            @change="enviarMidiaChatInterno"
          >

          <!-- Botão Criar Carrinho -->
          <button
            v-if="isMagazord && !cliente.is_group && !cliente.is_private_chat && !chatForaDaJanela"
            class="btn-attach-plus btn-carrinho"
            title="Criar Carrinho de Compras"
            @click="$emit('abrir-carrinho')"
          >
            <i class="feather icon-shopping-cart" />
          </button>

          <!-- T15/T17: "+" attachment button -->
          <div
            v-if="!chatForaDaJanela || cliente.is_group || cliente.is_private_chat"
            v-click-outside="fecharDropdownAnexos"
            class="dropdown-anexos-container"
          >
            <button
              id="tour-botao-anexos"
              class="btn-attach-plus"
              :disabled="cliente.atendimento_bot === true"
              @click="toggleDropdownAnexos"
            >
              <i class="feather icon-plus" />
            </button>
            <!-- T17: Attachment popup menu -->
            <transition name="popup-menu">
              <div
                v-if="dropdownAnexosAberto"
                class="attach-popup"
              >
                <div
                  class="attach-popup-item"
                  @click.prevent="abrirInputArquivoCorreto"
                >
                  <span class="attach-popup-icon attach-popup-icon--purple">
                    <i class="feather icon-file-text" />
                  </span>
                  <span class="attach-popup-label">Anexar arquivo/mídia</span>
                </div>
                <div
                  v-if="searchProducts"
                  class="attach-popup-item"
                  @click.prevent="fecharDropdownAnexos"
                >
                  <span class="attach-popup-icon attach-popup-icon--green">
                    <i class="feather icon-shopping-cart" />
                  </span>
                  <span class="attach-popup-label">Produto</span>
                </div>
              </div>
            </transition>
          </div>

          <!-- T15: Central zone — textarea + overlays -->
          <div
            class="chat-input-center"
          >
            <!-- T19: Reply inline banner -->
            <transition name="reply-slide">
              <div
                v-if="replyingTo"
                class="reply-banner"
              >
                <div class="reply-banner-bar" />
                <div class="reply-banner-body">
                  <div class="reply-banner-sender">
                    Respondendo
                  </div>
                  <div class="reply-banner-text">
                    {{ getReplyBannerPreview(replyingTo) }}
                  </div>
                </div>
                <button
                  class="reply-banner-close"
                  @click="replyingTo = null"
                >
                  <i class="feather icon-x" />
                </button>
              </div>
            </transition>

            <div
              v-if="!chatForaDaJanela || cliente.is_group || cliente.is_private_chat"
              class="input-with-magic position-relative"
            >
              <!-- Dropdown de sugestões da IA (lista vertical acima do input) -->
              <ul
                v-if="iaOpcoes.length > 0"
                class="list-group copiloto-dropdown"
                :style="copilotDropdownPositionStyle"
              >
                <li class="list-group-item copiloto-dropdown-header d-flex justify-content-between align-items-center">
                  <div class="d-flex flex-column">
                    <small class="text-muted font-weight-bold">Sugestões da IA</small>
                    <small
                      v-if="iaOpcoes.length > 1"
                      class="text-muted copiloto-header-sub"
                    >Profissional · Empático · Empolgado</small>
                  </div>
                  <button
                    class="btn btn-sm btn-link p-0 text-muted"
                    title="Fechar sugestões"
                    @click="fecharSugestoes"
                  >
                    <span style="font-size: 16px; font-weight: bold; line-height: 1;">×</span>
                  </button>
                </li>
                <li
                  v-for="opcao in iaOpcoes"
                  :key="opcao.id"
                  class="list-group-item list-group-item-action copiloto-sugestao-item"
                  @click="selecionarSugestaoIA(opcao)"
                >
                  <div
                    class="suggestion-text"
                    :class="{ 'expanded': opcao._expanded }"
                  >
                    {{ opcao.text }}
                  </div>
                  <div class="d-flex justify-content-between align-items-center mt-1 flex-wrap">
                    <small
                      class="badge badge-light text-muted copiloto-tone-badge"
                      :title="copilotToneTooltip(opcao)"
                    >{{ opcao.toneLabel }}</small>
                    <small
                      v-if="opcao.text.length > 60"
                      class="suggestion-expand text-primary"
                      @click.stop="toggleExpandSuggestion(opcao)"
                    >
                      {{ opcao._expanded ? 'Menos ▲' : 'Mais ▼' }}
                    </small>
                  </div>
                </li>
              </ul>

              <!-- Dropdown de atalhos rápidos (acima do input) -->
              <ul
                v-if="sugestoesFiltradas.length"
                class="list-group position-absolute w-100 atalhos-dropdown"
              >
                <li
                  v-for="(atalho, index) in sugestoesFiltradas"
                  :key="atalho.id"
                  :ref="'sugestaoItem_' + index"
                  class="list-group-item list-group-item-action"
                  :class="{ active: index === atalhoSelecionado }"
                  @mousedown.prevent="inserirAtalho(atalho)"
                >
                  <strong>{{ atalho.atalho }}</strong>
                  <small class="text-muted d-block text-truncate">{{ atalho.texto }}</small>
                </li>
              </ul>

              <textarea
                id="tour-input-mensagem"
                ref="inputMensagem"
                v-model="novaMensagem"
                class="form-control input-mensagem with-magic-wand"
                rows="1"
                :disabled="atendimentoResolvido || cliente.atendimento_bot || (chatForaDaJanela && !cliente.is_group && !cliente.is_private_chat)"
                :placeholder="placeholderMensagem"
                @input="ajustarAltura"
                @keydown.enter.prevent="confirmarAtalho($event)"
                @paste="handlePaste"
                @keydown.down="navegarAtalhoAbaixo"
                @keydown.up="navegarAtalhoAcima"
              />

              <!-- Varinha mágica dentro do input -->
              <button
                v-if="copilotoAtivo"
                class="magic-wand-btn"
                :disabled="iaCarregando || atendimentoResolvido || Boolean(cliente.atendimento_bot)"
                :title="iaCarregando ? 'Gerando...' : 'Usar copiloto'"
                @click="usarCopiloto"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 640"
                  style="width: 14px; height: 14px; fill: currentColor;"
                  :class="{ 'fa-spin': iaCarregando }"
                >
                  <path d="M295.4 37L310.2 73.8L347 88.6C350 89.8 352 92.8 352 96C352 99.2 350 102.2 347 103.4L310.2 118.2L295.4 155C294.2 158 291.2 160 288 160C284.8 160 281.8 158 280.6 155L265.8 118.2L229 103.4C226 102.2 224 99.2 224 96C224 92.8 226 89.8 229 88.6L265.8 73.8L280.6 37C281.8 34 284.8 32 288 32C291.2 32 294.2 34 295.4 37zM142.7 105.7L164.2 155.8L214.3 177.3C220.2 179.8 224 185.6 224 192C224 198.4 220.2 204.2 214.3 206.7L164.2 228.2L142.7 278.3C140.2 284.2 134.4 288 128 288C121.6 288 115.8 284.2 113.3 278.3L91.8 228.2L41.7 206.7C35.8 204.2 32 198.4 32 192C32 185.6 35.8 179.8 41.7 177.3L91.8 155.8L113.3 105.7C115.8 99.8 121.6 96 128 96C134.4 96 140.2 99.8 142.7 105.7zM496 368C502.4 368 508.2 371.8 510.7 377.7L532.2 427.8L582.3 449.3C588.2 451.8 592 457.6 592 464C592 470.4 588.2 476.2 582.3 478.7L532.2 500.2L510.7 550.3C508.2 556.2 502.4 560 496 560C489.6 560 483.8 556.2 481.3 550.3L459.8 500.2L409.7 478.7C403.8 476.2 400 470.4 400 464C400 457.6 403.8 451.8 409.7 449.3L459.8 427.8L481.3 377.7C483.8 371.8 489.6 368 496 368zM492 64C503 64 513.6 68.4 521.5 76.2L563.8 118.5C571.6 126.4 576 137 576 148C576 159 571.6 169.6 563.8 177.5L475.6 265.7L374.3 164.4L462.5 76.2C470.4 68.4 481 64 492 64zM76.2 462.5L340.4 198.3L441.7 299.6L177.5 563.8C169.6 571.6 159 576 148 576C137 576 126.4 571.6 118.5 563.8L76.2 521.5C68.4 513.6 64 503 64 492C64 481 68.4 470.4 76.2 462.5z" />
                </svg>
              </button>
            </div>

          </div>

          <!-- T15: Emoji standalone button (right of textarea) — only for normal clients -->
          <button
            v-if="!cliente.is_group && !cliente.is_private_chat && !chatForaDaJanela"
            class="btn-emoji-standalone"
            title="Emoji"
            @click.stop="toggleEmojiPicker"
          >
            <feather-icon
              icon="SmileIcon"
              size="18"
            />
          </button>

          <!-- T16: Send / Mic toggle button -->
          <div
            v-if="!chatForaDaJanela || cliente.is_group || cliente.is_private_chat"
            class="btn-send-mic"
          >
            <transition
              name="send-mic"
              mode="out-in"
            >
              <button
                v-if="novaMensagem.trim()"
                id="tour-botao-enviar"
                key="send"
                class="btn-send-mic-inner btn-send-mic-inner--send"
                :disabled="atendimentoResolvido || Boolean(cliente.atendimento_bot)"
                @click="enviarMensagem"
              >
                <feather-icon
                  icon="SendIcon"
                  size="18"
                />
              </button>
              <button
                v-else
                key="mic"
                class="btn-send-mic-inner btn-send-mic-inner--mic"
                :disabled="cliente.is_group || cliente.is_private_chat"
                @click="toggleGravacao"
              >
                <feather-icon
                  icon="MicIcon"
                  size="18"
                />
              </button>
            </transition>
          </div>

        </div>
      </template>

      <!-- T25: Emoji Picker (v-emoji-picker) -->
      <transition name="popup-menu">
        <div
          v-if="mostrarEmojis"
          v-click-outside="() => { mostrarEmojis = false }"
          class="emoji-picker-container"
        >
          <VEmojiPicker
            :show-search="true"
            :show-categories="true"
            :emojis-by-row="8"
            :continuous-list="true"
            :emoji-size="28"
            @select="onSelectEmoji"
          />
        </div>
      </transition>
    </div>

    <modal-midia
      :midia="midiaSelecionada"
      :visivel="modalMidiaVisivel"
      :lista="listaMidiasChat"
      :cliente="cliente"
      @fechar="modalMidiaVisivel = false"
      @responder="(msg) => { modalMidiaVisivel = false; prepararResposta(msg) }"
      @reagir="(msg, emoji) => reagir(msg, emoji)"
    />

    <!-- Modal de Pedidos removido - agora é gerenciado pelo componente pai -->

    <modal-selecao-templates
      v-model="modalTemplateVisivel"
      :templates="templatesDisponiveis"
      :carregando="false"
      :show-category="true"
      @template-selecionado="onTemplateCampanhaSelecionado"
    >
      <template #info-text>
        <strong>Apenas templates aprovados</strong> pelo WhatsApp estão disponíveis para iniciar conversas.
      </template>
      <template #empty-text>
        Crie templates e aguarde a aprovação do WhatsApp para utilizá-los.
      </template>
    </modal-selecao-templates>

    <!-- FULLSCREEN FILE PREVIEW OVERLAY bloquear dentro da janela de 24h -->
    <transition name="fade">
      <div
        v-if="previewFullscreen && !chatForaDaJanela"
        ref="previewOverlay"
        class="file-preview-overlay"
        tabindex="-1"
        @keydown.esc.stop="fecharPreviewFullscreen"
        @keydown.enter.exact.prevent="enviarPreviewFullscreen"
      >
        <button
          class="preview-close-btn"
          @click="fecharPreviewFullscreen"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </button>

        <div class="preview-main-area">
          <template v-if="previewFiles[previewActiveIndex]">
            <img
              v-if="previewFiles[previewActiveIndex].type.startsWith('image/')"
              :src="createPreviewUrl(previewFiles[previewActiveIndex])"
              class="preview-main-img"
            >
            <video
              v-else-if="previewFiles[previewActiveIndex].type.startsWith('video/')"
              :src="createPreviewUrl(previewFiles[previewActiveIndex])"
              controls
              class="preview-main-video"
            />
            <div
              v-else
              class="preview-main-doc"
            >
              <feather-icon
                icon="FileTextIcon"
                size="64"
                class="text-white"
              />
              <span class="preview-doc-name">{{ previewFiles[previewActiveIndex].name }}</span>
              <span class="preview-doc-size">{{ getFileSize(previewFiles[previewActiveIndex].size) }}</span>
            </div>
          </template>
        </div>

        <div class="preview-bottom-bar">
          <div
            v-if="previewFiles.length > 0"
            class="preview-thumbnails"
          >
            <div
              v-for="(file, idx) in previewFiles"
              :key="idx"
              class="preview-thumb"
              :class="{ 'preview-thumb--active': idx === previewActiveIndex }"
            >
              <button
                type="button"
                class="preview-thumb-remove"
                title="Remover arquivo"
                aria-label="Remover arquivo"
                @click.stop="removerArquivoDoPreview(idx)"
              >
                <feather-icon
                  icon="XIcon"
                  size="12"
                />
              </button>
              <div
                class="preview-thumb-inner"
                role="button"
                tabindex="0"
                @click="previewActiveIndex = idx"
                @keydown.enter.prevent="previewActiveIndex = idx"
              >
                <img
                  v-if="file.type.startsWith('image/')"
                  :src="createPreviewUrl(file)"
                  class="preview-thumb-img"
                >
                <feather-icon
                  v-else-if="file.type.startsWith('video/')"
                  icon="VideoIcon"
                  size="20"
                />
                <feather-icon
                  v-else
                  icon="FileTextIcon"
                  size="20"
                />
              </div>
            </div>

            <button
              type="button"
              class="preview-thumb preview-thumb--add"
              title="Adicionar arquivos"
              aria-label="Adicionar mais arquivos"
              @click="abrirSeletorPreviewAdicionarArquivos"
            >
              <feather-icon
                icon="PlusIcon"
                size="22"
              />
            </button>
          </div>

          <input
            ref="inputPreviewAdicionarArquivos"
            type="file"
            multiple
            class="d-none"
            :accept="previewMidiaAcceptAttr"
            @change="onPreviewAdicionarArquivos"
          >

          <div class="preview-caption-row">
            <textarea
              v-model="previewLegendaAtiva"
              class="preview-caption-input"
              :placeholder="previewFiles.length > 1 ? 'Legenda deste arquivo...' : 'Adicionar legenda...'"
              rows="1"
              @keydown.esc.stop="fecharPreviewFullscreen"
            />
            <button
              class="preview-send-btn"
              @click="enviarPreviewFullscreen"
            >
              <feather-icon
                icon="SendIcon"
                size="20"
              />
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Componente da Tour do Chat -->
    <app-tour
      v-if="stepsChatAtendimento && stepsChatAtendimento.length > 0"
      :steps="stepsChatAtendimento"
    />
  </div>
</template>

<script>
import AppTour from '@core/components/app-tour/AppTour.vue'
import { BAvatar } from 'bootstrap-vue'
import ModalSelecaoTemplates from '@core/components/modal-selecao-templates/ModalSelecaoTemplates.vue'
import { VEmojiPicker } from 'v-emoji-picker'
import { buscarMensagemPorReferencia } from '@/utils/chat-helpers'
import {
  sendMessage, enviarAnexo, resolveTicket, enviarTemplate, transcreverAudio, reactToMessage, removeReactionFromMessage, copilotSuggest,
  listarTemplates, vincularAtendente, buscaAtendentes,
} from '../../functions/basics/chat'
import ModalTemplates from './ModalTemplates.vue'
import ModalMidia from './ModalMidia.vue'
import CampoTags from './CampoTags.vue'
import PreviewPdf from './PreviewPdf.vue'
import { enviarMensagemGrupo, getMensagensGrupo, enviarMensagemAtendente } from '../../functions/basics/chat-interno'
import socket from '../../functions/services/socket.js'
import SeparadorTrocaAtendente from './components/SeparadorTrocaAtendente.vue'

export default {
  components: {
    ModalTemplates,
    ModalSelecaoTemplates,
    ModalMidia,
    CampoTags,
    PreviewPdf,
    BAvatar,
    AppTour,
    VEmojiPicker,
    SeparadorTrocaAtendente,
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutsideHandler = binding.value
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            const fn = el._clickOutsideHandler
            if (typeof fn === 'function') fn(event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      update(el, binding) {
        el._clickOutsideHandler = binding.value
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
        el._clickOutsideHandler = null
      },
    },
  },
  props: {
    cliente: Object,
    mensagens: Array,
    atendenteId: Number,
    tagsDisponiveis: Array,
    atalhosDisponiveis: {
      type: Array,
      default: () => [],
    },
    isMagazord: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tourChatCompleta: false,
      primeiraConversa: false,
      dropdownAberto: false,
      dropdownAnexosAberto: false,
      searchProducts: false,
      mostrarSugestoes: false,
      filtroAtalho: '',
      atalhoSelecionado: 0,
      // Drag and Drop properties
      isDragOver: false,
      dragPreviewFiles: [],
      showDragPreview: false,
      previewFullscreen: false,
      previewFiles: [],
      previewActiveIndex: 0,
      previewLegendas: [],
      velocidadeAudio: 1.0,
      resolverConfirmando: false,
      isPreviewPlaying: false,
      audioObjPreview: null,
      chatForaDaJanela: false,
      modalMidiaVisivel: false,
      modalTemplateVisivel: false, // Flag para controlar a visibilidade do modal de templates
      templatesDisponiveis: [], // Lista de templates disponíveis
      clienteSelecionado: null,
      atendentesGrupo: [], // Lista de atendentes para chat de grupo
      audioStyle: {
        backgroundColor: '#f5f0fb',
        border: '1px solid #e2d4ff',
        color: '#6f42c1',
        borderRadius: '8px',
        width: '100%',
      },
      audioAtual: null,
      audioObj: null,
      midiaSelecionada: null,
      novaMensagem: '',
      mostrarEmojis: false,
      emojis: ['😀', '😁', '😂', '😅', '😊', '😍', '😎', '😢', '😡', '🎉', '👍', '🙏'],
      reactionEmojis: ['👍', '👎', '❤️', '😂', '😮', '😢', '😡', '✅', '❌'],
      gravandoAudio: false,
      cronometro: 0,
      intervaloCronometro: null,
      mediaRecorder: null,
      chunks: [],
      stream: null,
      previewAudioBlob: null,
      previewAudioUrl: null,
      audioStatus: null,
      enviandoAudio: false,
      audioContext: null,
      analyserNode: null,
      waveformBars: new Array(30).fill(4),
      waveformAnimationId: null,
      maxRecordingTimeout: null,
      showScrollButton: false,
      replyingTo: null,
      copilotoAtivo: true,
      iaPersonalidade: 'neutra',
      iaOpcoes: [],
      iaCarregando: false,
      copilotDropdownPositionStyle: {},
      _copilotRepositionHandlers: null,
      corTagCache: new Map(),
      buscaInlineAtiva: false,
      termoBuscaInline: '',
      resultadosBusca: [],
      indiceResultadoAtual: -1,
      _buscaDebounceTimer: null,
      isHistoryLoad: true,
      clienteDigitando: false,
      typingTimeout: null,
      mensagensVisivelCount: 20,
      carregandoMaisMensagens: false,
      // Estado de UI por mensagem (mensagens são Object.freeze-adas no parent — mantemos os campos `_xxx` aqui).
      msgUiStates: {},
      // Overrides de reação por whatsapp_id (mensagens estão Object.freeze-adas, não dá pra escrever em msg.reaction).
      // Valor: { emoji, outbound } ou null para indicar reação removida.
      reactionOverrides: {},
      // Estado do player de áudio (apenas um áudio toca por vez).
      audioPlayingId: null,
      audioCurrentTime: 0,
      audioDuration: 0,
      audioProgress: 0,
    }
  },
  computed: {
    listaMidiasChat() {
      if (!this.mensagens || !Array.isArray(this.mensagens)) return []
      return this.mensagens.filter(m => (m.tipo === 'image' || m.tipo === 'video') && m.url)
    },
    /** Legenda do arquivo atual no preview (uma entrada por arquivo em previewLegendas). */
    previewLegendaAtiva: {
      get() {
        const i = this.previewActiveIndex
        const arr = this.previewLegendas || []
        return arr[i] != null ? arr[i] : ''
      },
      set(v) {
        const i = this.previewActiveIndex
        this.$set(this.previewLegendas, i, v != null ? String(v) : '')
      },
    },
    shouldAnimateBubbles() {
      return !this.isHistoryLoad && this.mensagensVisiveis.length < 100
    },
    placeholderMensagem() {
      // Mobile: texto simples, Desktop: texto com instruções
      return window.innerWidth < 768
        ? 'Digite sua mensagem...'
        : 'Shift + enter para nova linha e / para atalhos.'
    },
    /** Accept do file input do preview (mesmos tipos do drag-and-drop no WhatsApp). */
    previewMidiaAcceptAttr() {
      return [
        'image/jpeg',
        'image/png',
        'video/mp4',
        'video/3gpp',
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ].join(',')
    },
    mensagensOrdenadas() {
      if (!this.mensagens || !Array.isArray(this.mensagens)) {
        return []
      }
      // Criar cópia do array para não mutar o original
      const mensagensCopia = [...this.mensagens]

      // Ordenar por timestamp (mais antigas primeiro)
      const ordenadas = mensagensCopia.sort((a, b) => {
        const timestampA = this.getTimestampValue(a.timestamp)
        const timestampB = this.getTimestampValue(b.timestamp)
        return timestampA - timestampB
      })

      // Resolver referências pendentes que não foram encontradas no momento do recebimento
      // Isso ocorre quando a mensagem chegou via socket antes do histórico ser carregado
      return ordenadas.map(msg => {
        if (
          msg.referencia_mensagem_id
          && msg.mensagem_referenciada_nao_encontrada
          && !msg.mensagem_referenciada
        ) {
          const encontrada = buscarMensagemPorReferencia(ordenadas, msg.referencia_mensagem_id)
          if (encontrada) {
            return {
              ...msg,
              mensagem_referenciada: encontrada,
              mensagem_referenciada_nao_encontrada: false,
            }
          }
        }
        return msg
      })
    },
    mensagensVisiveis() {
      const total = this.mensagensOrdenadas.length
      const inicio = Math.max(0, total - this.mensagensVisivelCount)
      return this.mensagensOrdenadas.slice(inicio)
    },
    mostrarHistoricoConversasTopo() {
      if (!this.cliente || this.cliente.is_group || this.cliente.is_private_chat) return false
      return this.historicoTopoItens.length > 0
    },
    /** Linhas de data + cards de atendimentos anteriores + sessão carregada por citação (waId). */
    historicoTopoItens() {
      if (!this.cliente || this.cliente.is_group || this.cliente.is_private_chat) return []
      const currentId = String(this.cliente.id ?? '')
      const rows = []

      const anteriores = (this.cliente.historico && this.cliente.historico.atendimentos_anteriores) || []
      anteriores.forEach(a => {
        if (!a || String(a.atendimento_id) === currentId) return
        const ts = a.fim || a.inicio || a.data || a.timestamp
        const ult = a.ultima_mensagem
        const previewApi = ult && (ult.conteudo || ult.texto)
        rows.push({
          key: `ant-${a.atendimento_id}`,
          atendimento_id: a.atendimento_id,
          sortTs: this.getTimestampValue(ts),
          preview:
            previewApi
            || a.preview
            || a.last_message
            || a.resumo
            || 'Toque para ver esta conversa',
          fromCitacao: false,
        })
      })

      const cit = this.cliente.historico_citacao_sessao
      if (cit && cit.atendimento_id != null && String(cit.atendimento_id) !== currentId) {
        const msgs = cit.messages || []
        rows.push({
          key: `cit-${cit.atendimento_id}`,
          atendimento_id: cit.atendimento_id,
          sortTs: this.ultimoTsMongoMsgs(msgs),
          preview: this.previewHistoricoMongoMsgs(msgs),
          fromCitacao: true,
        })
      }

      const byTicket = new Map()
      rows.forEach(r => {
        const id = String(r.atendimento_id)
        const prev = byTicket.get(id)
        if (!prev || r.fromCitacao) byTicket.set(id, r)
      })
      const merged = Array.from(byTicket.values()).sort((a, b) => a.sortTs - b.sortTs)

      const out = []
      let lastDayKey = null
      merged.forEach(r => {
        const dayKey = this.formatDiaMesAnoHistorico(r.sortTs)
        if (dayKey !== lastDayKey) {
          out.push({ type: 'date', label: dayKey, key: `sep-${dayKey}-${r.key}` })
          lastDayKey = dayKey
        }
        out.push({
          type: 'card',
          key: r.key,
          atendimento_id: r.atendimento_id,
          preview: r.preview,
          hora: this.formatHoraSomenteHistorico(r.sortTs),
        })
      })
      return out
    },
    sugestoesFiltradas() {
      if (!this.novaMensagem || typeof this.novaMensagem !== 'string') {
        return []
      }
      const match = this.novaMensagem.match(/(\/[a-z0-9_-]*)$/i)
      if (!match) return []
      const termo = match[1].toLowerCase()
      return this.atalhosDisponiveis.filter(a => a.atalho.toLowerCase().startsWith(termo))
    },
    stepsChatAtendimento() {
      // Só mostrar tour se for primeira conversa e não foi completada
      if (!this.primeiraConversa || this.tourChatCompleta || this.cliente.is_group || this.cliente.is_private_chat) {
        return []
      }

      const steps = [
        {
          target: '#tour-nome-cliente',
          header: {
            title: '👤 Informações do Cliente',
          },
          content: 'Clique no nome do cliente para abrir o card com todas as informações, histórico de atendimentos, pedidos e muito mais!',
          params: {
            placement: 'bottom',
          },
        },
      ]

      // Adicionar step de etiquetas apenas se não for grupo ou chat privado
      if (!this.cliente.is_group && !this.cliente.is_private_chat) {
        steps.push({
          target: '#tour-botao-tags',
          header: {
            title: '🏷️ Atribuir Etiquetas',
          },
          content: 'Use etiquetas para categorizar e organizar suas conversas. Clique aqui para adicionar ou remover etiquetas do cliente.',
          params: {
            placement: 'bottom',
          },
        })

        steps.push({
          target: '#tour-dropdown-acoes',
          header: {
            title: '⚙️ Ações Disponíveis',
          },
          content: 'Acesse ações importantes: Ver Pedidos, Histórico, Resolver Atendimento, Vincular Atendente e muito mais!',
          params: {
            placement: 'bottom',
          },
        })
      }

      steps.push({
        target: '#tour-input-mensagem',
        header: {
          title: '💬 Enviar Mensagens',
        },
        content: 'Digite sua mensagem aqui. Use <strong>Shift + Enter</strong> para nova linha e <strong>/</strong> para acessar atalhos rápidos.',
        params: {
          placement: 'top',
        },
      })

      steps.push({
        target: '#tour-botao-enviar',
        header: {
          title: '📤 Enviar Mensagem',
        },
        content: 'Clique aqui ou pressione <strong>Enter</strong> para enviar sua mensagem. Você também pode enviar mídias, áudios e templates!',
        params: {
          placement: 'top',
        },
      })

      return steps
    },
    atendimentoResolvido() {
      return this.cliente && this.cliente.status === 'resolvido'
    },
    progressBarValue() {
      if (this.gravandoAudio) {
        return Math.min((this.cronometro / 60) * 100, 100)
      } if (this.audioObjPreview && this.audioObjPreview.duration) {
        return Math.min((this.audioObjPreview.currentTime / this.audioObjPreview.duration) * 100, 100)
      }
      return 0
    },
    previewAudioCurrentTime() {
      return this.audioObjPreview ? this.audioObjPreview.currentTime : 0
    },
  },
  watch: {
    'cliente.id': function () {
      if (this.previewFullscreen) this.fecharPreviewFullscreen()
    },
    previewFullscreen(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.previewOverlay && this.$refs.previewOverlay.focus()
        })
      }
    },
    novaMensagem(novoTexto) {
      const texto = novoTexto != null ? String(novoTexto) : ''
      if (!texto.trim()) {
        this.fecharSugestoes()
      }
      const match = texto.match(/\/([a-z0-9_-]*)$/i)
      if (match) {
        this.filtroAtalho = match[1].toLowerCase()
        this.mostrarSugestoes = this.sugestoesFiltradas.length > 0
      } else {
        this.mostrarSugestoes = false
      }
    },
    // Pré-popula o store de UI por mensagem (msgUiStates) sempre que a lista mudar,
    // garantindo que `audioUi(msg)` no template seja sempre leitura pura e que a
    // reatividade dos campos `_xxx` esteja estabelecida antes do primeiro render.
    mensagens: {
      immediate: true,
      handler(novas) {
        if (novas && Array.isArray(novas)) {
          for (let i = 0; i < novas.length; i += 1) {
            this.ensureMsgUi(novas[i])
          }
        }
        this.scrollToBottom()
        this.atualizarChatForaDaJanela()
        if (this.buscaInlineAtiva && this.termoBuscaInline.length >= 3) {
          this.executarBuscaInline()
        }
        setTimeout(() => {
          this.isHistoryLoad = false
        }, 100)
      },
    },
    termoBuscaInline(novoTermo) {
      clearTimeout(this._buscaDebounceTimer)
      if (novoTermo.length < 3) {
        this.resultadosBusca = []
        this.indiceResultadoAtual = -1
        return
      }
      this._buscaDebounceTimer = setTimeout(() => {
        this.executarBuscaInline()
      }, 300)
    },
    buscaInlineAtiva(ativo) {
      if (ativo) {
        this.$nextTick(() => {
          if (this.$refs.inputBuscaInline) {
            this.$refs.inputBuscaInline.focus()
          }
        })
      } else {
        this.termoBuscaInline = ''
        this.resultadosBusca = []
        this.indiceResultadoAtual = -1
      }
    },
    cliente: {
      immediate: true,
      handler(novoCliente, antigoCliente) {
        if (novoCliente && novoCliente.id !== antigoCliente?.id) {
          this.fecharSugestoes()
          this.isHistoryLoad = true
          this.clienteDigitando = false
          this.mensagensVisivelCount = 20
          this.fecharBuscaInline()
          this.$nextTick(() => {
            this.isHistoryLoad = false
            setTimeout(() => {
              this.scrollToBottom(true)
            }, 300)
          })
        }
      },
    },
  },
  created() {
    // Default estável compartilhado para mensagens sem id — evita criar `{}` novos por render.
    this._EMPTY_UI = Object.freeze({
      _progress: 0,
      _playing: false,
      _currentTime: 0,
      _duration: 0,
      _waveformData: null,
      _transcrevendo: false,
      _transcricao: null,
      _transcricaoComprida: false,
      _transcricaoExpandida: false,
      _showReactions: false,
    })
    // Map não-reativo de HTMLAudioElement por msg.id (DOM elements não devem ir no data reativo).
    this._audios = Object.create(null)
  },
  async mounted() {
    const el = this.$refs.chatBody
    if (el) el.addEventListener('scroll', this.handleScroll)
    this.atualizarChatForaDaJanela()

    // Carregar atendentes se for chat de grupo ou chat privado entre atendentes
    if (this.cliente.is_group || this.cliente.is_private_chat) {
      await this.carregarAtendentes()
    }

    // Configurar listeners de socket para chat interno
    this.configurarSocketChatInterno()

    // Listeners de reações (cliente reagiu / confirmação da reação enviada)
    socket.on('reaction-received', this._onReactionReceived)
    socket.on('reaction-sent', this._onReactionSent)

    // Scroll para o final quando montar o componente
    this.$nextTick(() => {
      setTimeout(() => {
        this.scrollToBottom(true)
      }, 500)
    })
  },
  beforeDestroy() {
    this.unbindCopilotDropdownReposition()
    const el = this.$refs.chatBody
    if (el) el.removeEventListener('scroll', this.handleScroll)

    // Remover listeners de socket
    socket.off('equipe-received-message', this.receberMensagemGrupo)
    socket.off('atendente-received-message', this.receberMensagemAtendente)
    socket.off('reaction-received', this._onReactionReceived)
    socket.off('reaction-sent', this._onReactionSent)
    socket.off('typing')
    socket.off('stop-typing')
    clearTimeout(this.typingTimeout)

    // Pausa e descarta Audio elements alocados para esta central
    if (this._audios) {
      Object.keys(this._audios).forEach(key => {
        const a = this._audios[key]
        if (a) {
          try { a.pause() } catch (e) { /* ignore */ }
          a.src = ''
        }
      })
      this._audios = Object.create(null)
    }
    this.audioPlayingId = null
    this.audioCurrentTime = 0
    this.audioDuration = 0
    this.audioProgress = 0
  },
  methods: {
    isOnlyEmojis(text) {
      if (!text || typeof text !== 'string') return false
      const emojiRegex = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200D\uFE0F]+$/u
      const trimmed = text.trim()
      if (!emojiRegex.test(trimmed)) return false
      const emojiCount = [...trimmed].filter(char => /\p{Emoji_Presentation}|\p{Extended_Pictographic}/u.test(char)).length
      return emojiCount >= 1 && emojiCount <= 3
    },
    handleEsc() {
      // Hierarquia de prioridade para ESC:
      // 1. Fecha preview fullscreen (imagem/vídeo)
      if (this.previewFullscreen) {
        this.fecharPreviewFullscreen()
        return true
      }
      // 2. Fecha preview de áudio gravado
      if (this.previewAudioUrl) {
        this.cancelarPreviewAudio()
        return true
      }
      // 3. Fecha sugestões do copiloto (evita lista “presa” sobre o input)
      if (this.iaOpcoes.length > 0) {
        this.fecharSugestoes()
        return true
      }
      // 4. Fecha reply banner
      if (this.replyingTo) {
        this.replyingTo = null
        return true
      }
      // Retorna false para indicar que não tratou o ESC
      return false
    },
    async carregarAtendentes() {
      try {
        const response = await buscaAtendentes(this.$route.params.uuid)
        this.atendentesGrupo = response.atendentes || []
      } catch (error) {
        console.error('Erro ao carregar atendentes:', error)
        this.atendentesGrupo = []
      }
    },
    getNomeCliente() {
      // Prioridade: cliente.cliente.name > cliente.nome > cliente.whatsapp > 'Cliente'
      if (this.cliente?.cliente?.name) {
        return this.cliente.cliente.name
      }
      if (this.cliente?.nome) {
        return this.cliente.nome
      }
      if (this.cliente?.cliente?.whatsapp) {
        return this.formatarCelular(this.cliente.cliente.whatsapp)
      }
      return 'Cliente'
    },
    configurarSocketChatInterno() {
      // Apenas configurar se for grupo ou chat privado
      if (!this.cliente.is_group && !this.cliente.is_private_chat) return

      console.log('🔌 Configurando socket para chat interno...', {
        isGroup: this.cliente.is_group,
        isPrivateChat: this.cliente.is_private_chat,
        clienteId: this.cliente.id,
        atendenteId: this.atendenteId,
      })

      // Entrar no room correto
      if (this.cliente.is_group) {
        // Para grupo: join no room do grupo
        socket.emit('chat_interno:join_grupo', {
          chatId: this.$route.params.uuid,
          userId: this.atendenteId,
        })
        console.log('📢 Emit: chat_interno:join_grupo', { chatId: this.$route.params.uuid })
      } else if (this.cliente.is_private_chat) {
        // Para chat privado: join no room individual
        socket.emit('chat_interno:join_individual', {
          chatId: this.$route.params.uuid,
          userId: this.atendenteId,
          receiverId: this.cliente.atendente_destinatario_id,
        })
        console.log('📢 Emit: chat_interno:join_individual', {
          chatId: this.$route.params.uuid,
          receiverId: this.cliente.atendente_destinatario_id,
        })
      }

      // Listener para QUALQUER evento (debug)
      const originalOnevent = socket.onevent
      socket.onevent = function (packet) {
        const args = packet.data || []
        console.log('🔔 Evento socket recebido:', args[0], args[1])
        originalOnevent.call(this, packet)
      }

      // Listener para mensagens de grupo
      socket.on('equipe-received-message', this.receberMensagemGrupo)

      // Listener para mensagens individuais entre atendentes
      socket.on('atendente-received-message', this.receberMensagemAtendente)

      console.log('✅ Socket chat interno configurado!')
      console.log('📋 Listeners ativos:', socket.listeners('equipe-received-message').length, socket.listeners('atendente-received-message').length)
    },
    receberMensagemGrupo(payload) {
      console.log('📩 Mensagem de grupo recebida via socket:', payload)
      console.log('🔍 Estado atual:', {
        isGroup: this.cliente.is_group,
        clienteId: this.cliente.id,
        payloadChatId: payload.chat_id,
        match: this.cliente.is_group && payload.chat_id === this.cliente.id,
      })

      // Verificar se é para este grupo
      if (this.cliente.is_group && payload.chat_id === this.cliente.id) {
        // Verificar se a mensagem já existe (evitar duplicatas)
        const jaExiste = this.mensagens.some(m => m._id === payload._id || m.id === payload._id)
        if (jaExiste) {
          console.log('⚠️ Mensagem já existe, ignorando...')
          return
        }

        // Adicionar a nova mensagem
        const novaMensagem = {
          id: payload._id,
          _id: payload._id,
          texto: payload.text || '',
          text: payload.text || '',
          tipo: payload.type,
          type: payload.type,
          remetente: 'atendente',
          sender_id: payload.sender_id,
          timestamp: payload.timestamp,
          hora: payload.timestamp,
          is_group: true,
          url: payload.url,
          mediaType: payload.type,
        }

        this.mensagens.push(novaMensagem)
        console.log('✅ Mensagem de grupo adicionada!')

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    receberMensagemAtendente(payload) {
      console.log('📩 Mensagem de atendente recebida:', payload)

      // Verificar se é para este chat privado
      if (this.cliente.is_private_chat) {
        const meuId = this.atendenteId
        const destinatarioId = this.cliente.atendente_destinatario_id

        // Verificar se a mensagem é para mim ou de mim
        const paraMin = payload.receiver_id === meuId && payload.sender_id === destinatarioId
        const deMim = payload.sender_id === meuId && payload.receiver_id === destinatarioId

        if (paraMin || deMim) {
          // Verificar se a mensagem já existe
          const jaExiste = this.mensagens.some(m => m._id === payload._id || m.id === payload._id)
          if (jaExiste) {
            console.log('⚠️ Mensagem já existe, ignorando...')
            return
          }

          // Adicionar a nova mensagem
          const novaMensagem = {
            id: payload._id,
            _id: payload._id,
            texto: payload.text || '',
            text: payload.text || '',
            tipo: payload.type,
            type: payload.type,
            remetente: 'atendente',
            sender_id: payload.sender_id,
            receiver_id: payload.receiver_id,
            timestamp: payload.timestamp,
            hora: payload.timestamp,
            is_private_chat: true,
            is_group: false,
            url: payload.url,
            mediaType: payload.type,
          }

          this.mensagens.push(novaMensagem)
          console.log('✅ Mensagem de atendente adicionada!')

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      }
    },
    getNomeAtendente(senderId) {
      if ((!this.cliente.is_group && !this.cliente.is_private_chat) || !senderId) return null
      // Buscar atendente pelo id ou external_id (sender_id pode ser qualquer um)
      const atendente = this.atendentesGrupo.find(a => a.id === senderId || a.external_id === senderId)
      return atendente ? atendente.name : `Usuário ${senderId}`
    },
    isMinhaMensagem(senderId) {
      if (!senderId) return false

      // Buscar meu ID do localStorage (external_id)
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const meuExternalId = userData.id

      // Buscar o atendente pelo senderId para pegar seu external_id
      const atendente = this.atendentesGrupo.find(a => a.id === senderId)

      if (!atendente) {
        return false
      }

      // Comparar external_id do atendente com meu external_id do localStorage
      return atendente.external_id === meuExternalId
    },
    deveAlinharDireita(msg) {
      // Se for grupo ou chat privado entre atendentes, verificar se é minha mensagem pelo sender_id
      if ((this.cliente.is_group || this.cliente.is_private_chat) && msg.sender_id) {
        return this.isMinhaMensagem(msg.sender_id)
      }
      // Se não for grupo nem chat privado, usar lógica padrão (atendente/bot à direita)
      return msg.remetente === 'atendente' || msg.remetente === 'bot'
    },
    getTipoBubble(msg) {
      // Se for grupo ou chat privado, determinar tipo pela verificação de mensagem
      if ((this.cliente.is_group || this.cliente.is_private_chat) && msg.sender_id) {
        // Se é minha mensagem, usar bubble-atendente (azul)
        if (this.isMinhaMensagem(msg.sender_id)) {
          return 'atendente'
        }
        // Se não é minha, usar bubble-cliente (branco)
        return 'cliente'
      }
      // Se não for grupo nem chat privado, usar o remetente original
      return msg.remetente
    },
    getTimestampValue(timestamp) {
      // Se já for um número, retorna diretamente
      if (typeof timestamp === 'number') {
        return timestamp
      }

      // Se for um objeto do MongoDB ($timestamp)
      if (timestamp && typeof timestamp === 'object' && timestamp.$timestamp) {
        return Number(timestamp.$timestamp)
      }

      // Se for uma string de data
      if (typeof timestamp === 'string') {
        return new Date(timestamp).getTime()
      }

      // Se for um objeto Date
      if (timestamp instanceof Date) {
        return timestamp.getTime()
      }

      // Fallback: retorna 0
      return 0
    },
    scrollParaItemSelecionado() {
      this.$nextTick(() => {
        const refKey = `sugestaoItem_${this.atalhoSelecionado}`
        const ref = this.$refs[refKey]
        const el = Array.isArray(ref) ? ref[0] : ref

        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({
            block: 'nearest',
            behavior: 'auto', // ou 'smooth' se quiser animado
          })
        } else {
          console.warn('Elemento de atalho não encontrado:', refKey, ref)
        }
      })
    },
    navegarAtalhoAbaixo(event) {
      if (this.sugestoesFiltradas.length) {
        event.preventDefault()
        this.atalhoSelecionado = (this.atalhoSelecionado + 1) % this.sugestoesFiltradas.length
        this.scrollParaItemSelecionado()
      }
    },
    navegarAtalhoAcima(event) {
      if (this.sugestoesFiltradas.length) {
        event.preventDefault()
        this.atalhoSelecionado = (this.atalhoSelecionado - 1 + this.sugestoesFiltradas.length) % this.sugestoesFiltradas.length
        this.scrollParaItemSelecionado()
      }
    },
    confirmarAtalho(event) {
      if (event.shiftKey) {
        // Permite quebra de linha
        this.novaMensagem += '\n'
        return
      }

      if (this.sugestoesFiltradas.length) {
        const atalho = this.sugestoesFiltradas[this.atalhoSelecionado]
        if (atalho) {
          this.novaMensagem = this.novaMensagem.replace(/(\/[a-z0-9_-]*)$/i, atalho.texto)
          this.$nextTick(() => this.$refs.inputMensagem.focus())
          this.atalhoSelecionado = 0
          this.ajustarAltura && this.ajustarAltura()
        }
      } else {
        this.enviarMensagem()
      }
    },
    inserirAtalho(atalho) {
      this.novaMensagem = this.novaMensagem.replace(/(\/[a-z0-9_-]*)$/i, atalho.texto)
      this.$nextTick(() => this.$refs.inputMensagem.focus())
      this.atalhoSelecionado = 0
      this.ajustarAltura && this.ajustarAltura()
    },
    // Chave estável para identificar uma mensagem (algumas vêm da API com `_id`,
    // outras do socket com `id`, e respostas pendentes só têm `whatsapp_id`).
    msgKey(msg) {
      if (!msg) return null
      if (msg.id != null) return msg.id
      if (msg._id != null) return msg._id
      if (msg.whatsapp_id != null) return msg.whatsapp_id
      return null
    },
    // Estado de UI reativo por mensagem. As próprias mensagens são Object.freeze-adas
    // no parent — qualquer campo `_xxx` (progresso, transcrição, picker de reações…) vive aqui.
    // Pré-populado pelo watcher de `mensagens`, então este getter é puro (sem mutação durante render).
    audioUi(msg) {
      const key = this.msgKey(msg)
      if (key == null) return this._EMPTY_UI
      const ui = this.msgUiStates[key]
      if (ui) return ui
      this.ensureMsgUi(msg)
      return this.msgUiStates[key] || this._EMPTY_UI
    },
    ensureMsgUi(msg) {
      const key = this.msgKey(msg)
      if (key == null) return
      if (this.msgUiStates[key]) return
      this.$set(this.msgUiStates, key, {
        _progress: 0,
        _playing: false,
        _currentTime: 0,
        _duration: 0,
        _waveformData: null,
        _transcrevendo: false,
        _transcricao: null,
        _transcricaoComprida: false,
        _transcricaoExpandida: false,
        _showReactions: false,
      })
    },
    // Audio element fica fora do store reativo — guardamos por msgKey num map não-reativo.
    getAudioEl(msg) {
      const key = this.msgKey(msg)
      return (this._audios && key != null) ? this._audios[key] : null
    },
    setAudioEl(msg, audio) {
      const key = this.msgKey(msg)
      if (key == null) return
      if (!this._audios) this._audios = Object.create(null)
      this._audios[key] = audio
    },
    async transcreverAudio(msg) {
      const ui = this.audioUi(msg)
      if (ui._transcrevendo) return

      ui._transcrevendo = true
      ui._transcricao = null

      try {
        const response = await transcreverAudio(msg.url)
        const texto = response.data.transcricaoAudio || 'Não foi possível transcrever o áudio.'
        ui._transcricao = texto

        this.$nextTick(() => {
          const el = this.$refs.transcricaoContent
          if (el && el.scrollHeight > el.clientHeight + 5) {
            ui._transcricaoComprida = true
          } else {
            ui._transcricaoComprida = false
          }
          ui._transcricaoExpandida = false
        })
      } catch (err) {
        ui._transcricao = 'Erro ao transcrever o áudio.'
        console.error('Erro ao transcrever:', err)
      } finally {
        ui._transcrevendo = false
      }
    },
    togglePlayAudio(msg) {
      const key = this.msgKey(msg)
      if (key == null) return
      this.ensureMsgUi(msg)
      const ui = this.msgUiStates[key]
      if (ui && !ui._waveformData) this.gerarWaveformData(msg)

      let audio = this.getAudioEl(msg)
      const isPlayingThis = this.audioPlayingId === key

      if (isPlayingThis && audio) {
        audio.pause()
        this.audioPlayingId = null
        return
      }

      // Pausa qualquer outro áudio em execução
      this.pausarTodosAudios()

      if (!audio) {
        audio = new Audio(msg.url)
        audio.preload = 'auto'
        this.setAudioEl(msg, audio)

        audio.addEventListener('loadedmetadata', () => {
          const dur = audio.duration || 0
          const cached = this.msgUiStates[key]
          if (cached) cached._duration = dur
          if (this.audioPlayingId === key) {
            this.audioDuration = dur
          }
        })

        audio.addEventListener('timeupdate', () => {
          if (this.audioPlayingId !== key) return
          this.audioCurrentTime = audio.currentTime
          this.audioDuration = audio.duration || 0
          this.audioProgress = audio.duration
            ? (audio.currentTime / audio.duration) * 100
            : 0
        })

        audio.addEventListener('ended', () => {
          if (this.audioPlayingId === key) {
            this.audioPlayingId = null
            this.audioCurrentTime = 0
            this.audioProgress = 0
          }
        })
      }

      this.audioPlayingId = key
      this.audioCurrentTime = audio.currentTime || 0
      this.audioDuration = audio.duration || 0
      this.audioProgress = audio.duration
        ? (audio.currentTime / audio.duration) * 100
        : 0
      audio.playbackRate = this.velocidadeAudio || 1.0
      const p = audio.play()
      if (p && typeof p.catch === 'function') {
        p.catch(err => {
          console.warn('[audio] play() falhou:', err)
          if (this.audioPlayingId === key) this.audioPlayingId = null
        })
      }
    },

    pausarTodosAudios() {
      const audios = this._audios || {}
      Object.keys(audios).forEach(key => {
        const a = audios[key]
        if (a) {
          try { a.pause() } catch (e) { /* ignore */ }
        }
      })
      this.audioPlayingId = null
    },

    formatTime(segundos) {
      const m = Math.floor(segundos / 60).toString().padStart(1, '0')
      const s = Math.floor(segundos % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },

    alternarVelocidadeAudio() {
      const velocidades = [1.0, 1.2, 1.5, 2.0]
      const atual = velocidades.indexOf(this.velocidadeAudio)
      this.velocidadeAudio = velocidades[(atual + 1) % velocidades.length]

      const audios = this._audios || {}
      Object.keys(audios).forEach(key => {
        if (audios[key]) audios[key].playbackRate = this.velocidadeAudio
      })
    },
    async gerarWaveformData(msg) {
      const ui = this.audioUi(msg)
      if (ui._waveformData) return
      try {
        const response = await fetch(msg.url)
        const buffer = await response.arrayBuffer()
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        const audioBuffer = await audioCtx.decodeAudioData(buffer)
        const channelData = audioBuffer.getChannelData(0)
        const bars = 40
        const segmentSize = Math.floor(channelData.length / bars)
        const waveform = []
        for (let i = 0; i < bars; i++) {
          let sum = 0
          const start = i * segmentSize
          for (let j = start; j < start + segmentSize && j < channelData.length; j++) {
            sum += Math.abs(channelData[j])
          }
          waveform.push(sum / segmentSize)
        }
        const max = Math.max(...waveform, 0.01)
        const normalized = waveform.map(v => v / max)
        ui._waveformData = normalized
        audioCtx.close()
      } catch (e) {
        console.warn('Waveform decode failed, using fallback:', e)
        const seed = typeof msg.id === 'number' ? msg.id : String(msg.id).split('').reduce((a, c) => a + c.charCodeAt(0), 0)
        const fallback = []
        let s = seed
        for (let i = 0; i < 40; i++) {
          s = (s * 16807 + 11) % 2147483647
          fallback.push(0.15 + (s % 1000) / 1000 * 0.85)
        }
        ui._waveformData = fallback
      }
    },
    seekAudioWaveform(msg, event) {
      const audio = this.getAudioEl(msg)
      if (!audio || !audio.duration) return
      const rect = event.currentTarget.getBoundingClientRect()
      const pos = (event.clientX - rect.left) / rect.width
      audio.currentTime = pos * audio.duration
      const key = this.msgKey(msg)
      if (this.audioPlayingId === key) {
        this.audioCurrentTime = audio.currentTime
        this.audioProgress = pos * 100
      }
    },
    confirmarResolucao() {
      if (this.chatForaDaJanela) {
        this.$bvModal.msgBoxConfirm(
          'Deseja realmente resolver essa conversa?\n\n⚠️ Não será enviado o CSAT porque passaram mais de 24 horas desde a última mensagem.',
          {
            title: 'Confirmar resolução sem CSAT',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Sim, resolver',
            cancelTitle: 'Cancelar',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        ).then(confirmed => {
          if (confirmed) this.resolverAtendimento()
        }).catch(() => {})
      } else {
        this.resolverAtendimento()
      }
    },
    iniciarWaveform() {
      if (!this.stream) return
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.analyserNode = this.audioContext.createAnalyser()
        this.analyserNode.fftSize = 64
        const source = this.audioContext.createMediaStreamSource(this.stream)
        source.connect(this.analyserNode)
        this.atualizarWaveform()
      } catch (e) {
        console.warn('Web Audio API não disponível:', e)
      }
    },
    atualizarWaveform() {
      if (!this.analyserNode || !this.gravandoAudio) return
      const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount)
      this.analyserNode.getByteFrequencyData(dataArray)
      const bars = []
      const step = Math.floor(dataArray.length / 30)
      for (let i = 0; i < 30; i++) {
        const value = dataArray[i * step] || 0
        const height = Math.max(4, (value / 255) * 28)
        bars.push(height)
      }
      this.waveformBars = bars
      this.waveformAnimationId = requestAnimationFrame(() => this.atualizarWaveform())
    },
    pararWaveform() {
      if (this.waveformAnimationId) {
        cancelAnimationFrame(this.waveformAnimationId)
        this.waveformAnimationId = null
      }
      if (this.audioContext) {
        this.audioContext.close().catch(() => {})
        this.audioContext = null
      }
      this.analyserNode = null
      this.waveformBars = new Array(30).fill(4)
    },
    cancelarGravacao() {
      this.pararWaveform()
      if (this.maxRecordingTimeout) {
        clearTimeout(this.maxRecordingTimeout)
        this.maxRecordingTimeout = null
      }
      if (this.gravandoAudio) {
        this.gravandoAudio = false
        clearInterval(this.intervaloCronometro)
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop()
        }
      }

      // Parar stream de áudio
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }

      // Limpar preview
      this.previewAudioBlob = null
      this.previewAudioUrl = null
      this.audioStatus = null
      this.cronometro = 0
      this.isPreviewPlaying = false
      this.chunks = []
      this.intervaloCronometro = null
      this.mediaRecorder = null

      // Parar áudio de preview
      if (this.audioObjPreview) {
        this.audioObjPreview.pause()
        this.audioObjPreview = null
      }
    },
    togglePlayPreview() {
      if (!this.audioObjPreview && this.previewAudioUrl) {
        this.audioObjPreview = new Audio(this.previewAudioUrl)
        this.audioObjPreview.playbackRate = this.velocidadeAudio

        this.audioObjPreview.addEventListener('ended', () => {
          this.isPreviewPlaying = false
        })

        // previewAudioCurrentTime é computed, não precisa atribuir valor manualmente
      }

      if (this.isPreviewPlaying) {
        this.audioObjPreview.pause()
        this.isPreviewPlaying = false
      } else {
        this.audioObjPreview.play()
        this.isPreviewPlaying = true
      }
    },
    verificarEnvio(e) {
      if (e.shiftKey) {
        // Permite quebra de linha com Shift+Enter
        this.novaMensagem += '\n'
      } else {
        this.enviarMensagem()
      }
    },
    ajustarAltura() {
      this.$nextTick(() => {
        const textarea = this.$refs.inputMensagem
        if (!textarea) return

        textarea.style.height = 'auto'
        const scrollAltura = textarea.scrollHeight
        const alturaMax = 120
        const novaAltura = Math.max(40, Math.min(scrollAltura, alturaMax))

        textarea.style.height = `${novaAltura}px`
        textarea.style.overflowY = scrollAltura > alturaMax ? 'auto' : 'hidden'

        if (this.iaOpcoes.length > 0) {
          this.positionCopilotDropdown()
        }
      })
    },
    // atualizarChatForaDaJanela() {
    //   const ultima = this.getUltimoHorarioCliente(this.mensagens)
    //   this.chatForaDaJanela = ultima
    //     ? ((new Date() - new Date(ultima)) / 3600000) > 24
    //     : false
    // },
    interromperAgente() {
      this.$bvModal.msgBoxConfirm('Deseja interromper o bot e assumir o atendimento?', {
        title: 'Interromper Bot',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Sim, interromper',
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(confirmed => {
        if (confirmed) {
          this.$emit('interromper-agente', this.cliente.id)
        }
      })
    },
    removerTag(tag) {
      const novasTags = this.cliente.tags.filter(t => t !== tag)
      this.$emit('atualizar-tags', novasTags)
    },
    getCorTag(nomeTag) {
      // Cache para cores de tags
      if (this.corTagCache.has(nomeTag)) {
        return this.corTagCache.get(nomeTag)
      }

      const tag = this.tagsDisponiveis.find(t => t.nome === nomeTag)
      const cor = (tag && tag.cor) ? tag.cor : '#6c757d'
      this.corTagCache.set(nomeTag, cor)
      return cor
    },
    atualizarChatForaDaJanela() {
      const ultimaMensagemCliente = this.getUltimoHorarioCliente(this.mensagens)
      // Se não há mensagem do cliente, está fora da janela (só pode enviar template)
      if (!ultimaMensagemCliente) {
        this.chatForaDaJanela = true
        return
      }

      const ultimaMensagem = new Date(ultimaMensagemCliente)
      const agora = new Date()
      const diffHoras = (agora - ultimaMensagem) / 1000 / 60 / 60
      this.chatForaDaJanela = diffHoras > 24
    },
    async enviarTemplate(templateName, templateBody) {
      const res = await enviarTemplate(this.$route.params.uuid, templateName, this.cliente.cliente.id)

      if (res.status === 200) {
        this.chatForaDaJanela = false // ATUALIZA A INTERFACE
        this.novaMensagem = templateBody
        this.enviarMensagem()
        this.$bvToast.toast(
          'O chat ficará ativo somente quando o cliente responder.',
          {
            title: 'Seu template foi enviado! ',
            variant: 'success',
            solid: true,
          },
        )
      }

      return res
    },
    abrirMidia(midia) {
      if (!midia) return
      const t = midia.tipo
      if (t === 'document' || t === 'pdf' || t === 'arquivo') {
        this.baixarArquivoMensagem(midia)
        return
      }
      this.midiaSelecionada = midia
      this.modalMidiaVisivel = true
    },
    nomeArquivoDownload(msg) {
      if (msg.filename && String(msg.filename).trim()) return String(msg.filename).trim()
      const src = msg.url || ''
      const fromUrl = src.split('/').pop()
      const base = (fromUrl || '').split('?')[0]
      if (base) return base
      return `arquivo.${this.getDocExtension(msg)}`
    },
    baixarArquivoMensagem(msg) {
      const url = msg && msg.url
      if (!url) {
        this.$bvToast.toast('Link do arquivo não está disponível.', {
          title: 'Download',
          variant: 'warning',
          solid: true,
        })
        return
      }
      const nome = this.nomeArquivoDownload(msg)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', nome)
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
      document.body.removeChild(link)
    },
    getUltimoHorarioCliente(mensagens) {
      if (!Array.isArray(mensagens)) {
        throw new Error('O parâmetro deve ser um array de mensagens.')
      }

      const mensagensCliente = mensagens
        .filter(msg => msg.remetente === 'cliente')
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // ordem decrescente

      return mensagensCliente.length > 0 ? mensagensCliente[0].timestamp : null
    },
    async abrirModalTemplate() {
      const templatesDisponiveis = await listarTemplates(this.$route.params.uuid, true) // Apenas aprovados
      this.templatesDisponiveis = templatesDisponiveis.templates
      this.modalTemplateVisivel = true
    },
    onTemplateCampanhaSelecionado(payload) {
      if (payload && payload.name) {
        this.enviarTemplate(payload.name, payload.content || '')
      }
    },
    abrirModalPedidos(cliente) {
      // Emitir evento para o componente pai abrir o modal
      this.$emit('ver-pedidos', cliente.id)
    },

    toggleEmojiPicker() {
      this.mostrarEmojis = !this.mostrarEmojis
      this.fecharDropdownAnexos()
    },
    onSelectEmoji(emoji) {
      const emojiChar = emoji.data || emoji.emoji || emoji
      this.novaMensagem += emojiChar
      this.$nextTick(() => {
        if (this.$refs.inputMensagem) {
          this.$refs.inputMensagem.focus()
        }
      })
      this.salvarEmojiRecente(emojiChar)
    },
    salvarEmojiRecente(emoji) {
      try {
        let recentes = JSON.parse(localStorage.getItem('emojis_recentes') || '[]')
        recentes = recentes.filter(e => e !== emoji)
        recentes.unshift(emoji)
        recentes = recentes.slice(0, 20)
        localStorage.setItem('emojis_recentes', JSON.stringify(recentes))
      } catch (e) {
        // ignore localStorage errors
      }
    },
    abrirInputArquivo() {
      this.$refs.inputArquivo.click()
      this.fecharDropdownAnexos()
    },
    abrirInputArquivoCorreto() {
      // Se for grupo ou chat privado entre atendentes, usar input específico do chat interno
      if (this.cliente.is_group || this.cliente.is_private_chat) {
        this.$refs.inputArquivoChatInterno.click()
      } else {
        // Para clientes normais, usar input padrão
        this.$refs.inputArquivo.click()
      }
      this.fecharDropdownAnexos()
    },
    async enviarAnexos(event) {
      const arquivos = event.target.files
      if (!arquivos.length) return

      const IMAGE_MIMES = new Set(['image/jpeg', 'image/png'])
      const VIDEO_MIMES = new Set(['video/mp4', 'video/3gpp'])
      const DOCUMENT_MIMES = new Set([
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ])
      const IMAGE_MAX_SIZE = 5 * 1024 * 1024
      const VIDEO_MAX_SIZE = 16 * 1024 * 1024
      const DOCUMENT_MAX_SIZE = 100 * 1024 * 1024

      const isAccepted = mime => IMAGE_MIMES.has(mime)
        || VIDEO_MIMES.has(mime)
        || mime.startsWith('audio/')
        || DOCUMENT_MIMES.has(mime)

      const validFiles = []
      for (const arquivo of Array.from(arquivos)) {
        if (arquivo.type.startsWith('image/') && !IMAGE_MIMES.has(arquivo.type)) {
          this.$bvToast.toast(`Formato de imagem não suportado: "${arquivo.name}". Use apenas JPEG ou PNG.`, {
            title: 'Formato não aceito',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        if (arquivo.type.startsWith('video/') && !VIDEO_MIMES.has(arquivo.type)) {
          this.$bvToast.toast(`Formato de vídeo não suportado: "${arquivo.name}". Use apenas MP4 ou 3GPP.`, {
            title: 'Formato não aceito',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        if (!isAccepted(arquivo.type)) {
          this.$bvToast.toast(`Tipo de arquivo não suportado: "${arquivo.name}". Use imagens (JPEG/PNG), vídeos (MP4/3GPP), áudios, PDF, Word, Excel, PowerPoint ou TXT.`, {
            title: 'Arquivo não aceito',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        if (IMAGE_MIMES.has(arquivo.type) && arquivo.size > IMAGE_MAX_SIZE) {
          this.$bvToast.toast(`A imagem "${arquivo.name}" excede o limite de 5MB.`, {
            title: 'Imagem muito grande',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        if (VIDEO_MIMES.has(arquivo.type) && arquivo.size > VIDEO_MAX_SIZE) {
          this.$bvToast.toast(`O vídeo "${arquivo.name}" excede o limite de 16MB.`, {
            title: 'Vídeo muito grande',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        if (DOCUMENT_MIMES.has(arquivo.type) && arquivo.size > DOCUMENT_MAX_SIZE) {
          this.$bvToast.toast(`O documento "${arquivo.name}" excede o limite de 100MB.`, {
            title: 'Documento muito grande',
            variant: 'warning',
            solid: true,
          })
          continue
        }
        validFiles.push(arquivo)
      }
      if (validFiles.length === 0) return

      this.previewFiles = validFiles
      this.previewActiveIndex = 0
      this.previewLegendas = validFiles.map(() => '')
      this.previewFullscreen = true
    },
    /**
     * Insere texto no composer (e opcionalmente envia) a partir do modal de carrinho.
     */
    async inserirMensagemDoCarrinho(texto, enviarAutomatico = false) {
      const t = texto != null ? String(texto) : ''
      if (!t.trim()) {
        this.$bvToast.toast('Mensagem vazia.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.novaMensagem = t
      await this.$nextTick()
      if (this.$refs.inputMensagem) {
        this.$refs.inputMensagem.focus()
        if (typeof this.ajustarAltura === 'function') {
          this.ajustarAltura()
        }
      }
      if (enviarAutomatico) {
        await this.enviarMensagem()
      }
    },
    async enviarMensagem() {
      if (!this.novaMensagem.trim()) return

      // ✅ Se for chat de grupo, usar API específica de grupo
      if (this.cliente.is_group) {
        try {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}')
          const meuExternalId = userData.id // Este é o external_id

          // Buscar o atendente que tem esse external_id para pegar o id correto
          const meuAtendente = this.atendentesGrupo.find(a => a.external_id === meuExternalId)
          const meuAtendenteId = meuAtendente ? meuAtendente.id : null

          if (!meuAtendenteId) {
            this.$bvToast.toast('Erro: Não foi possível identificar seu usuário.', {
              title: 'Erro',
              variant: 'danger',
              solid: true,
            })
            console.error('❌ Atendente não encontrado para external_id:', meuExternalId)
            return
          }

          const payload = {
            senderId: meuAtendenteId, // Usar o id do atendente
            text: this.novaMensagem,
          }

          const response = await enviarMensagemGrupo(this.$route.params.uuid, payload)

          // Criar objeto da nova mensagem
          const novaMensagem = {
            id: response._id || Date.now(),
            texto: this.novaMensagem,
            text: this.novaMensagem,
            tipo: 'text',
            type: 'texto',
            remetente: 'atendente',
            sender_id: meuAtendenteId,
            timestamp: Date.now(),
            is_group: true,
          }

          // Adicionar mensagem ao array local (reativo)
          this.mensagens.push(novaMensagem)

          // Forçar atualização do componente
          this.$forceUpdate()

          // Scroll para o final
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        } catch (error) {
          console.error('❌ Erro ao enviar mensagem para grupo:', error)
          this.$bvToast.toast('Erro ao enviar mensagem para o grupo.', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
          return
        }

        this.novaMensagem = ''
        this.scrollToBottom()
        return
      }

      // ✅ Se for chat privado entre atendentes, usar API específica de chat direto
      if (this.cliente.is_private_chat) {
        try {
          const payload = {
            senderId: this.atendenteId,
            text: this.novaMensagem,
          }

          const response = await enviarMensagemAtendente(
            this.atendenteId,
            this.cliente.atendente_destinatario_id,
            this.$route.params.uuid,
            payload,
          )

          // Criar objeto da nova mensagem usando os dados da resposta
          const novaMensagem = {
            id: response._id,
            texto: response.text,
            text: response.text,
            tipo: response.type === 'texto' ? 'text' : response.type,
            type: response.type,
            remetente: 'atendente',
            sender_id: response.sender_id,
            receiver_id: response.receiver_id,
            timestamp: response.timestamp,
            is_private_chat: true,
            is_group: false,
            url: response.url,
          }

          // Adicionar mensagem ao array local
          this.mensagens.push(novaMensagem)

          // Forçar atualização do componente
          this.$forceUpdate()

          // Scroll para o final
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        } catch (error) {
          console.error('❌ Erro ao enviar mensagem direta:', error)
          this.$bvToast.toast('Erro ao enviar mensagem.', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
          return
        }

        this.novaMensagem = ''
        this.scrollToBottom()
        return
      }

      // 👉 Verifica e vincula atendente se ainda for atendimento livre (apenas para chat de cliente)
      if (this.cliente.atendenteId === null) {
        try {
          const response = await vincularAtendente(this.cliente.id, this.atendenteId, this.$route.params.uuid)
          if (response.status === 200) {
            this.cliente.atendenteId = this.atendenteId
            this.$emit('atualizar-atendente', this.atendenteId)
            this.$emit('atualizar-atual-atendente', { atendimentoId: this.cliente.id, novoAtendenteId: novoId })
          }
        } catch (error) {
          console.error('Erro ao vincular atendente:', error)
        }
      }

      // Gerar mensagemId local para rastreamento (permite receber whatsapp_id via socket depois)
      const mensagemIdLocal = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

      // Preparar payload para sendMessage
      const payload = {
        ticketId: this.cliente.id,
        sender: 'atendente',
        message: this.novaMensagem,
        to: this.cliente.cliente.whatsapp,
        accountId: this.$route.params.uuid,
        type: 'text',
        url: null,
        atendenteId: this.atendenteId,
        mensagemId: mensagemIdLocal,
      }

      // Se estiver respondendo a uma mensagem, adicionar replyToMessageId
      if (this.replyingTo) {
        const replyWaId = this.replyingTo.whatsapp_id || this.replyingTo.mensagemId
        if (replyWaId) {
          payload.replyToMessageId = replyWaId
        }
        console.log('[ChatAtendimento] Reply payload:', {
          replyToMessageId: payload.replyToMessageId || '(não definido)',
          whatsapp_id: this.replyingTo.whatsapp_id,
          mensagemId: this.replyingTo.mensagemId,
        })
      }

      try {
        const response = await sendMessage(payload)

        // Preparar objeto da mensagem
        const novaMensagemObj = {
          id: Date.now(),
          texto: this.novaMensagem,
          tipo: 'texto',
          remetente: 'atendente',
          mensagemId: mensagemIdLocal,
          whatsapp_id: null,
          timestamp: new Date().toISOString(),
        }

        // Se estiver respondendo a uma mensagem, adicionar dados da referência
        if (this.replyingTo) {
          const refId = this.replyingTo.whatsapp_id || this.replyingTo.mensagemId || `reply_${Date.now()}`
          novaMensagemObj.referencia_mensagem_id = refId
          novaMensagemObj.mensagem_referenciada = this.mensagemReferenciadaParaEnvio(this.replyingTo)
          novaMensagemObj.mensagem_referenciada_nao_encontrada = false
        }

        this.mensagens.push(novaMensagemObj)
        console.log(this.mensagens)
      } catch (e) {
        this.$bvToast.toast('Falha ao enviar a mensagem.', { title: 'Erro', variant: 'danger', solid: true })
        return
      } finally {
        // Limpar replyingTo após enviar
        this.replyingTo = null
      }

      this.$emit('mensagem-enviada', this.cliente.id)
      this.novaMensagem = ''
      this.$nextTick(() => {
        const ta = this.$refs.inputMensagem
        if (ta) { ta.style.height = '40px'; ta.style.overflowY = 'hidden' }
      })
      this.scrollToBottom()
    },
    toggleGravacao() {
      if (this.gravandoAudio) {
        this.pararWaveform()
        if (this.maxRecordingTimeout) {
          clearTimeout(this.maxRecordingTimeout)
          this.maxRecordingTimeout = null
        }
        this.gravandoAudio = false
        clearInterval(this.intervaloCronometro)
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop()
        }
        this.intervaloCronometro = null
        this.cronometro = 0
      } else {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
          this.stream = stream
          this.mediaRecorder = new MediaRecorder(stream)
          this.chunks = []

          this.mediaRecorder.ondataavailable = e => this.chunks.push(e.data)
          this.mediaRecorder.onstop = () => {
            const tipo = MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')
              ? 'audio/ogg; codecs=opus'
              : 'audio/webm'
            const blob = new Blob(this.chunks, { type: tipo })
            this.previewAudioBlob = blob
            this.previewAudioUrl = URL.createObjectURL(blob)
            this.stream.getTracks().forEach(track => track.stop())
            this.chunks = []
          }

          this.mediaRecorder.start()
          this.gravandoAudio = true
          this.cronometro = 0
          this.intervaloCronometro = setInterval(() => this.cronometro++, 1000)
          this.iniciarWaveform()

          this.maxRecordingTimeout = setTimeout(() => {
            if (this.gravandoAudio) {
              this.toggleGravacao()
              this.$bvToast.toast('Gravação parada automaticamente após 5 minutos.', {
                title: 'Limite de gravação',
                variant: 'warning',
                solid: true,
              })
            }
          }, 5 * 60 * 1000)
        }).catch(error => {
          console.error('Erro ao acessar microfone:', error)
          this.$bvToast.toast('Não foi possível acessar o microfone. Verifique as permissões.', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        })
      }
    },
    async enviarAudioGravado() {
      // Proteção contra cliques múltiplos
      if (this.enviandoAudio) {
        console.warn('⚠️ Envio de áudio já em andamento, aguarde...')
        return
      }

      // Se ainda está gravando, parar primeiro e aguardar o blob
      if (this.gravandoAudio) {
        this.toggleGravacao()
        // Aguardar um momento para o blob ser criado
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      // Validar se o blob existe
      if (!this.previewAudioBlob) {
        console.error('❌ Blob de áudio não disponível')
        this.$bvToast.toast('Erro ao processar o áudio. Tente gravar novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        return
      }

      // Marcar como enviando para prevenir duplicação
      this.enviandoAudio = true
      this.audioStatus = 'enviando'

      const tipoMime = this.previewAudioBlob.type || 'audio/webm'
      const extensao = tipoMime.includes('ogg') ? 'ogg' : 'webm'
      const arquivo = new File([this.previewAudioBlob], `audio.${extensao}`, { type: tipoMime })

      try {
        // Upload do arquivo
        const { url } = await enviarAnexo(arquivo, e => {
          this.audioProgresso = Math.round((e.loaded * 100) / e.total)
        })

        // Enviar mensagem
        const mensagemIdLocal = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        const payload = {
          ticketId: this.cliente.id,
          sender: 'atendente',
          to: this.cliente.cliente.whatsapp,
          message: null,
          accountId: this.$route.params.uuid,
          type: 'audio',
          url,
          atendenteId: this.atendenteId,
          mensagemId: mensagemIdLocal,
        }

        await sendMessage(payload)

        // Adicionar mensagem na lista
        this.mensagens.push({
          id: Date.now(),
          tipo: 'audio',
          remetente: 'atendente',
          hora: new Date().toISOString(),
          url,
          status: 'enviado',
          mensagemId: mensagemIdLocal,
          whatsapp_id: null,
          timestamp: new Date().toISOString(),
        })

        this.scrollToBottom()
        this.limparPreviewAudio()

        this.$bvToast.toast('Áudio enviado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
        })
      } catch (e) {
        console.error('❌ Erro ao enviar áudio:', e)
        this.audioStatus = 'erro'
        this.$bvToast.toast('Erro ao enviar o áudio. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        // Sempre liberar a flag de envio
        this.enviandoAudio = false
      }
    },

    cancelarPreviewAudio() {
      this.previewAudioBlob = null
      this.previewAudioUrl = null
      this.audioStatus = null
    },
    limparPreviewAudio() {
      // Limpar URL do preview para liberar memória
      if (this.previewAudioUrl) {
        URL.revokeObjectURL(this.previewAudioUrl)
      }

      this.previewAudioBlob = null
      this.previewAudioUrl = null
      this.audioStatus = null
      this.cronometro = 0
      this.chunks = []
      this.isPreviewPlaying = false
      this.enviandoAudio = false

      // Limpar áudio de preview
      if (this.audioObjPreview) {
        this.audioObjPreview.pause()
        this.audioObjPreview = null
      }

      // Limpar MediaRecorder
      if (this.mediaRecorder) {
        this.mediaRecorder = null
      }

      // Limpar stream
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
    },
    async enviarMidiaChatInterno(event, legendaEnvio = '') {
      const arquivo = event.target.files[0]
      if (!arquivo) return

      const textoLegenda = legendaEnvio != null ? String(legendaEnvio).trim() : ''

      // Validar tamanho (máximo 100MB)
      const tamanhoMaxMB = 100
      const tamanhoMaxBytes = tamanhoMaxMB * 1024 * 1024

      if (arquivo.size > tamanhoMaxBytes) {
        this.$bvToast.toast(`O arquivo excede o tamanho máximo de ${tamanhoMaxMB}MB`, {
          title: 'Arquivo muito grande',
          variant: 'warning',
          solid: true,
        })
        event.target.value = null
        return
      }

      // Importar as funções necessárias
      const { enviarMidiaGrupo, enviarMidiaIndividual } = require('../../functions/basics/chat-interno')

      // Criar mensagem temporária
      const tipoMidia = this.getTipoMidiaArquivo(arquivo)
      const msgTemporaria = {
        id: Date.now(),
        tipo: tipoMidia,
        type: tipoMidia,
        remetente: 'atendente',
        hora: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        status: 'enviando',
        texto: 'Enviando mídia...',
        progresso: 0,
      }

      this.mensagens.push(msgTemporaria)
      this.scrollToBottom()

      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const meuExternalId = userData.id

        let resposta

        if (this.cliente.is_group) {
          // Chat de grupo
          const meuAtendente = this.atendentesGrupo.find(a => a.external_id === meuExternalId)
          const meuAtendenteId = meuAtendente ? meuAtendente.id : null

          if (!meuAtendenteId) {
            throw new Error('Não foi possível identificar seu usuário.')
          }

          resposta = await enviarMidiaGrupo(
            this.$route.params.uuid,
            arquivo,
            textoLegenda,
            meuAtendenteId,
            progressEvent => {
              msgTemporaria.progresso = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            },
          )
        } else if (this.cliente.is_private_chat) {
          // Chat individual
          resposta = await enviarMidiaIndividual(
            this.$route.params.uuid,
            this.cliente.atendente_destinatario_id,
            arquivo,
            textoLegenda,
            this.atendenteId,
            progressEvent => {
              msgTemporaria.progresso = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            },
          )
        }

        if (resposta && resposta.mensagem) {
          Object.assign(msgTemporaria, {
            id: resposta.mensagem._id || msgTemporaria.id,
            _id: resposta.mensagem._id,
            texto: resposta.mensagem.text || '',
            text: resposta.mensagem.text || '',
            tipo: resposta.mensagem.type,
            type: resposta.mensagem.type,
            sender_id: resposta.mensagem.sender_id,
            timestamp: resposta.mensagem.timestamp || new Date().toISOString(),
            hora: resposta.mensagem.timestamp || new Date().toISOString(),
            is_group: resposta.mensagem.is_group,
            is_private_chat: this.cliente.is_private_chat,
            url: resposta.mediaUrl || resposta.mensagem.url,
            mediaType: resposta.mediaType,
            status: 'enviado',
          })

          console.log('✅ Mensagem atualizada:', msgTemporaria)
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })

        this.$bvToast.toast('Mídia enviada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('❌ Erro ao enviar mídia no chat interno:', error)

        // Atualizar mensagem com erro
        const index = this.mensagens.findIndex(m => m.id === msgTemporaria.id)
        if (index !== -1) {
          this.mensagens[index].status = 'erro'
        }

        this.$bvToast.toast(error.message || 'Erro ao enviar mídia. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }

      // Limpar input
      event.target.value = null
    },
    getTipoMidiaArquivo(arquivo) {
      const tipo = arquivo.type
      if (tipo.startsWith('image/')) return 'image'
      if (tipo.startsWith('video/')) return 'video'
      if (tipo.startsWith('audio/')) return 'audio'
      if (tipo === 'application/pdf') return 'document'
      return 'file'
    },
    buildCopilotConversationHistory() {
      const list = this.mensagensOrdenadas || []
      const max = 40
      return list
        .slice(-max)
        .map(m => ({
          remetente: m.remetente === 'cliente' ? 'cliente' : 'atendente',
          texto: String(m.texto ?? m.text ?? m.conteudo ?? '').trim(),
          tipo: m.tipo || m.type || 'text',
          timestamp: m.timestamp || m.hora || '',
        }))
        .filter(x => x.texto)
    },
    /** Normaliza `tone` da API (ex.: profissional / empático / empolgado) — ver COPILOT_SUGGEST_FRONTEND.md */
    copilotToneDisplay(toneRaw) {
      const s = String(toneRaw ?? '').trim().toLowerCase()
      const key = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const map = {
        profissional: { label: 'Profissional', hint: 'Formal moderado, objetivo' },
        empatico: { label: 'Empático', hint: 'Acolhedor, cordial' },
        empolgado: { label: 'Empolgado', hint: 'Entusiasmado, sem exagero' },
      }
      const row = map[key]
      if (row) return row
      if (!toneRaw) return { label: 'Variação', hint: '' }
      return { label: String(toneRaw), hint: '' }
    },
    copilotToneTooltip(opcao) {
      if (!opcao) return ''
      const parts = []
      if (opcao.toneHint) parts.push(opcao.toneHint)
      if (opcao.promptUsed) parts.push(opcao.promptUsed)
      return parts.join(' · ')
    },
    async usarCopiloto() {
      if (!this.copilotoAtivo) return

      const draft = (this.novaMensagem || '').trim()
      const type = draft ? 'improve' : 'create'
      let message = draft || undefined
      if (message && message.length > 1000) {
        message = message.slice(0, 1000)
      }

      const contextParts = []
      const nome = this.cliente?.cliente?.nome
      if (nome) contextParts.push(`Cliente: ${nome}`)
      if (this.replyingTo) {
        const prev = this.getReplyBannerPreview(this.replyingTo)
        if (prev) contextParts.push(`Respondendo a: ${prev}`)
      }
      const context = contextParts.join('. ')

      const atendimentoId = this.cliente?.id != null ? String(this.cliente.id) : null

      let conversationHistory
      if (!atendimentoId && this.mensagensOrdenadas?.length) {
        conversationHistory = this.buildCopilotConversationHistory()
      }

      const category =
        this.cliente?.categoria ??
        this.cliente?.category ??
        this.cliente?.copilot_category ??
        null

      try {
        this.iaCarregando = true
        const res = await copilotSuggest({
          type,
          message,
          context,
          category,
          personality: this.iaPersonalidade || 'neutra',
          atendimentoId,
          conversationHistory,
          options: 3,
        })

        if (!res || !res.success) {
          throw new Error(res?.error || 'Copilot falhou')
        }

        const data = res.data || {}
        let opcoes = []
        if (Array.isArray(data.options) && data.options.length > 0) {
          opcoes = data.options.map(o => {
            const td = this.copilotToneDisplay(o.tone)
            return {
              id: o.id,
              text: o.text,
              tone: o.tone,
              toneLabel: td.label,
              toneHint: td.hint,
              promptUsed: o.promptUsed != null ? String(o.promptUsed) : '',
              _expanded: false,
            }
          })
        } else if (data.suggestion != null && String(data.suggestion).trim() !== '') {
          opcoes = [{
            id: 1,
            text: String(data.suggestion).trim(),
            tone: data.type || '',
            toneLabel: 'Sugestão',
            toneHint: '',
            promptUsed: '',
            _expanded: false,
          }]
        }

        this.iaOpcoes = opcoes
        if (opcoes.length > 0) {
          this.scheduleCopilotDropdownPosition()
          this.bindCopilotDropdownReposition()
        } else {
          this.unbindCopilotDropdownReposition()
          this.copilotDropdownPositionStyle = {}
          this.$bvToast.toast('Nenhuma sugestão gerada. Tente novamente.', {
            title: 'Copiloto', variant: 'warning', solid: true, autoHideDelay: 3000,
          })
        }
      } catch (e) {
        const apiErr = e.response?.data?.error || e.message
        this.$bvToast.toast(apiErr || 'Falha ao obter sugestões da IA.', {
          title: 'Erro', variant: 'danger', solid: true,
        })
      } finally {
        this.iaCarregando = false
      }
    },
    toggleExpandSuggestion(opcao) {
      this.$set(opcao, '_expanded', !opcao._expanded)
    },
    selecionarSugestaoIA(opcao) {
      if (!opcao || !opcao.text) return
      this.novaMensagem = opcao.text
      this.fecharSugestoes()
      this.$nextTick(() => {
        this.ajustarAlturaParaSugestao(opcao.text)
        this.$refs.inputMensagem && this.$refs.inputMensagem.focus()
      })
    },

    ajustarAlturaParaSugestao(texto) {
      const linhas = texto.split('\n').length
      const linhasEstimadas = Math.ceil(texto.length / 50) // Estima quebras de linha por largura
      const totalLinhas = Math.max(linhas, linhasEstimadas)
      const alturaMaxima = Math.min(totalLinhas, 3) // Máximo 3 linhas

      this.$nextTick(() => {
        const textarea = this.$refs.inputMensagem
        if (textarea) {
          textarea.style.height = 'auto'
          textarea.rows = alturaMaxima
          textarea.style.height = `${textarea.scrollHeight}px`
        }
      })
    },

    scheduleCopilotDropdownPosition() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.positionCopilotDropdown()
        })
      })
    },
    positionCopilotDropdown() {
      const textarea = this.$refs.inputMensagem
      if (!textarea || !this.iaOpcoes.length) {
        this.copilotDropdownPositionStyle = {}
        return
      }
      const rect = textarea.getBoundingClientRect()
      const gap = 4
      this.copilotDropdownPositionStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + gap}px`,
        zIndex: 1060,
      }
    },
    bindCopilotDropdownReposition() {
      this.unbindCopilotDropdownReposition()
      const onMove = () => {
        this.positionCopilotDropdown()
      }
      window.addEventListener('resize', onMove)
      window.addEventListener('scroll', onMove, true)
      this._copilotRepositionHandlers = { onMove }
    },
    unbindCopilotDropdownReposition() {
      if (!this._copilotRepositionHandlers) return
      const { onMove } = this._copilotRepositionHandlers
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
      this._copilotRepositionHandlers = null
    },
    fecharSugestoes() {
      this.unbindCopilotDropdownReposition()
      this.copilotDropdownPositionStyle = {}
      this.iaOpcoes = []
    },

    onCopilotoToggle() {
      if (!this.copilotoAtivo) {
        this.fecharSugestoes()
      }
    },
    resolverAtendimento() {
      resolveTicket(this.cliente.id, this.$route.params.uuid, this.cliente.cliente.id, this.chatForaDaJanela).then(() => {
        // Notificação de sucesso quando o próprio usuário resolve o atendimento
        this.$bvToast.toast('Atendimento resolvido com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        // atendimentoResolvido é computed, será atualizado automaticamente quando cliente.status mudar

        // Limpar cache do carrinho quando atendimento for resolvido
        const chaveCache = `carrinho_cliente_${this.cliente.id}`
        localStorage.removeItem(chaveCache)
        console.log('🗑️ Cache do carrinho removido para atendimento resolvido:', this.cliente.id)

        // Emitir evento para fechar a conversa
        this.$emit('atendimento-resolvido')
      }).catch(error => {
        // Notificação de erro caso a resolução falhe
        console.error('Erro ao resolver atendimento:', error)
        this.$bvToast.toast('Erro ao resolver atendimento. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      })
    },
    // scrollToBottom() {
    //   this.$nextTick(() => {
    //     const el = this.$refs.chatBody
    //     if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    //   })
    // },
    scrollToBottom(force = false) {
      const el = this.$refs.chatBody
      if (!el) return

      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
      if (isNearBottom || force) {
        this.$nextTick(() => {
          el.scrollTop = el.scrollHeight
        })
      }
    },
    isIdOutgoing(whatsappId) {
      if (!whatsappId) return false
      // IDs de mensagens outgoing (enviadas pelo sistema/atendente) contêm 'FQIAERg' no padrão WhatsApp
      return whatsappId.includes('FQIAERg')
    },
    scrollParaMensagemReferenciada(msg) {
      if (!msg.referencia_mensagem_id) return

      const refId = String(msg.referencia_mensagem_id).trim()
      const ehWaIdWhatsapp = refId.startsWith('wamid.')

      // Resposta a mensagem antiga (fora do payload atual): abrir modal de histórico
      if (msg.mensagem_referenciada_nao_encontrada && ehWaIdWhatsapp) {
        const sessao = this.cliente?.historico_citacao_sessao
        const temSessao = sessao
          && sessao.atendimento_id
          && Array.isArray(sessao.messages)
          && sessao.messages.some(m => String(m.whatsapp_id ?? '').trim() === refId)
        this.$emit('abrir-historico-por-reply', {
          waId: refId,
          atendimentoId: temSessao ? sessao.atendimento_id : null,
        })
        return
      }

      if (msg.mensagem_referenciada_nao_encontrada) {
        this.$bvToast.toast('A mensagem referenciada não está disponível nesta conversa', {
          title: 'Mensagem não encontrada',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      // Buscar o elemento da mensagem referenciada pelo data-whatsapp-id
      this.$nextTick(() => {
        const { chatBody } = this.$refs
        if (!chatBody) return

        const encontrarPorWaId = (root, waId) => {
          const nodes = root.querySelectorAll('[data-whatsapp-id]')
          for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i]
            if ((el.getAttribute('data-whatsapp-id') || '').trim() === waId) return el
          }
          return null
        }

        const mensagemElement = encontrarPorWaId(chatBody, refId)

        if (mensagemElement) {
          // Travar overflow dos ancestrais para impedir propagação de scroll
          const ancestors = []
          let parent = chatBody.parentElement
          while (parent && parent !== document.documentElement) {
            const original = parent.style.overflow
            ancestors.push({ el: parent, overflow: original })
            parent.style.overflow = 'hidden'
            parent = parent.parentElement
          }

          const containerRect = chatBody.getBoundingClientRect()
          const elementRect = mensagemElement.getBoundingClientRect()
          const targetScrollTop = chatBody.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2)

          chatBody.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          })

          // Restaurar overflow dos ancestrais após a animação
          setTimeout(() => {
            ancestors.forEach(({ el, overflow }) => {
              el.style.overflow = overflow
            })
          }, 600)

          // Destacar a mensagem temporariamente
          mensagemElement.classList.add('mensagem-destacada')
          setTimeout(() => {
            mensagemElement.classList.remove('mensagem-destacada')
          }, 2000)
        } else if (ehWaIdWhatsapp) {
          const sessao = this.cliente?.historico_citacao_sessao
          const temSessao = sessao
            && sessao.atendimento_id
            && Array.isArray(sessao.messages)
            && sessao.messages.some(m => String(m.whatsapp_id ?? '').trim() === refId)
          this.$emit('abrir-historico-por-reply', {
            waId: refId,
            atendimentoId: temSessao ? sessao.atendimento_id : null,
          })
        } else {
          this.$bvToast.toast('Mensagem não disponível no histórico carregado', {
            title: 'Mensagem não encontrada',
            variant: 'warning',
            solid: true,
            autoHideDelay: 3000,
          })
        }
      })
    },
    handleScroll() {
      const el = this.$refs.chatBody
      if (!el) return
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 100
      this.showScrollButton = !nearBottom

      if (
        el.scrollTop < 150
        && !this.carregandoMaisMensagens
        && this.mensagensVisivelCount < this.mensagensOrdenadas.length
      ) {
        this.carregarMaisMensagensVisiveis()
      }
    },
    carregarMaisMensagensVisiveis() {
      if (this.carregandoMaisMensagens) return
      const el = this.$refs.chatBody
      if (!el) return
      this.carregandoMaisMensagens = true
      const prevScrollHeight = el.scrollHeight
      this.mensagensVisivelCount = Math.min(
        this.mensagensVisivelCount + 10,
        this.mensagensOrdenadas.length,
      )
      this.$nextTick(() => {
        el.scrollTop = el.scrollHeight - prevScrollHeight
        this.carregandoMaisMensagens = false
      })
    },
    tagStyle(tag) {
      const cores = {
        prioridade: '#E53935',
        reclamação: '#FB8C00',
        vip: '#43A047',
        elogio: '#1E88E5',
        financeiro: '#6D4C41',
      }
      const cor = cores[tag.toLowerCase()] || '#7367F0'
      return {
        backgroundColor: cor,
        color: '#fff',
        fontWeight: '500',
        fontSize: '0.75rem',
        padding: '4px 8px',
        borderRadius: '12px',
      }
    },
    formatHoraData(timestamp) {
      if (!timestamp) return ''
      const data = new Date(timestamp)
      return data.toLocaleString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDiaMesAnoHistorico(ts) {
      const n = typeof ts === 'number' ? ts : this.getTimestampValue(ts)
      if (!n) return '—'
      const d = new Date(n)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatHoraSomenteHistorico(ts) {
      const n = typeof ts === 'number' ? ts : this.getTimestampValue(ts)
      if (!n) return ''
      const d = new Date(n)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    previewHistoricoMongoMsgs(msgs) {
      if (!Array.isArray(msgs) || msgs.length === 0) return 'Sem mensagens'
      const m = [...msgs].reverse().find(x => x && x.tipo !== 'troca_atendente')
      if (!m) return 'Sem mensagens'
      const t = String(m.texto || m.conteudo || '').trim()
      if (t) return t.length > 140 ? `${t.slice(0, 137)}...` : t
      const tipo = String(m.tipo || '').toLowerCase()
      if (tipo === 'image') return 'Imagem'
      if (tipo === 'video') return 'Vídeo'
      if (tipo === 'audio') return 'Áudio'
      if (tipo === 'document' || tipo === 'pdf' || tipo === 'arquivo') return 'Documento'
      return 'Mensagem'
    },
    ultimoTsMongoMsgs(msgs) {
      if (!Array.isArray(msgs) || msgs.length === 0) return 0
      let max = 0
      msgs.forEach(m => {
        if (!m || m.tipo === 'troca_atendente') return
        const v = this.getTimestampValue(m.timestamp)
        if (v > max) max = v
      })
      return max
    },
    abrirHistoricoAtendimentoDoTopo(atendimentoId) {
      if (atendimentoId == null || atendimentoId === '') return
      this.$emit('abrir-historico-atendimento', atendimentoId)
    },
    getStatusIcon(status) {
      const icons = {
        enviando: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8696a0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
        enviado: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8696a0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><polyline points="20 6 9 17 4 12"/></svg>',
        entregue: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8696a0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><polyline points="18 6 7 17 2 12"/><polyline points="22 6 11 17 8 14"/></svg>',
        lido: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34B7F1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle"><polyline points="18 6 7 17 2 12"/><polyline points="22 6 11 17 8 14"/></svg>',
        erro: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline;vertical-align:middle" title="Erro no envio"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
      }
      return icons[status] || ''
    },
    voltarParaLista() {
      this.$emit('voltar-para-lista')
    },
    toggleDropdown() {
      this.dropdownAberto = !this.dropdownAberto
      // Fechar dropdown de tags quando abrir o de ações
      if (this.dropdownAberto) {
        this.fecharDropdownTags()
      }
    },
    fecharDropdown() {
      this.dropdownAberto = false
    },
    fecharDropdownTags() {
      // Fechar o dropdown de tags via ref
      if (this.$refs.campoTags && this.$refs.campoTags.$refs.dropdown) {
        this.$refs.campoTags.$refs.dropdown.hide()
      }
    },
    toggleDropdownAnexos() {
      this.dropdownAnexosAberto = !this.dropdownAnexosAberto
      this.mostrarEmojis = false
    },
    fecharDropdownAnexos() {
      this.dropdownAnexosAberto = false
    },
    executarAcao(acao) {
      this.fecharDropdown()

      switch (acao) {
        case 'pedidos':
          this.abrirModalPedidos(this.cliente)
          break
        case 'resolver':
          if (!(!this.chatForaDaJanela && (this.atendimentoResolvido || this.cliente.atendimento_bot))) {
            this.confirmarResolucao()
          }
          break
        case 'interromper':
          this.interromperAgente()
          break
        case 'template':
          this.abrirModalTemplate()
          break
      }
    },
    avatarUrl(nome) {
      const seed = encodeURIComponent(nome || 'user')
      return `https://api.dicebear.com/7.x/thumbs/svg?seed=${seed}`
    },
    abrirCardCliente() {
      this.$emit('abrir-card-cliente')
    },

    // DRAG AND DROP METHODS
    handleDragOver(event) {
      if (event.dataTransfer.types.includes('Files')) {
        event.preventDefault()
        this.isDragOver = true
      }
    },

    handleDragLeave(event) {
      // Só remove o drag over se sair completamente da zona
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOver = false
      }
    },

    handleDrop(event) {
      const files = Array.from(event.dataTransfer.files)

      if (files.length === 0) return // Drag de texto — deixa o browser inserir nativamente

      event.preventDefault()
      this.isDragOver = false

      const supportedFiles = this.filtrarArquivosMidiaCliente(files)

      if (supportedFiles.length === 0) {
        this.$bvToast.toast('Apenas imagens (JPEG/PNG), vídeos (MP4/3GPP) e documentos (PDF, Word, Excel, PowerPoint, TXT) são aceitos!', {
          title: 'Formato não suportado',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.previewFiles = supportedFiles
      this.previewActiveIndex = 0
      this.previewLegendas = supportedFiles.map(() => '')
      this.previewFullscreen = true
    },

    /** Mesmas regras do drop na conversa WhatsApp (imagens, vídeos, documentos). */
    filtrarArquivosMidiaCliente(files) {
      const IMAGE_MIMES_DROP = new Set(['image/jpeg', 'image/png'])
      const VIDEO_MIMES_DROP = new Set(['video/mp4', 'video/3gpp'])
      const DOCUMENT_MIMES_DROP = new Set([
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ])
      const IMAGE_MAX_SIZE_DROP = 5 * 1024 * 1024
      const VIDEO_MAX_SIZE_DROP = 16 * 1024 * 1024
      const DOCUMENT_MAX_SIZE_DROP = 100 * 1024 * 1024

      return files.filter(file => {
        const { type } = file
        if (type.startsWith('image/')) {
          if (!IMAGE_MIMES_DROP.has(type)) {
            this.$bvToast.toast(`Formato de imagem não suportado: "${file.name}". Use apenas JPEG ou PNG.`, {
              title: 'Formato não aceito',
              variant: 'warning',
              solid: true,
            })
            return false
          }
          if (file.size > IMAGE_MAX_SIZE_DROP) {
            this.$bvToast.toast(`A imagem "${file.name}" excede o limite de 5MB.`, {
              title: 'Imagem muito grande',
              variant: 'warning',
              solid: true,
            })
            return false
          }
          return true
        }
        if (type.startsWith('video/')) {
          if (!VIDEO_MIMES_DROP.has(type)) {
            this.$bvToast.toast(`Formato de vídeo não suportado: "${file.name}". Use apenas MP4 ou 3GPP.`, {
              title: 'Formato não aceito',
              variant: 'warning',
              solid: true,
            })
            return false
          }
          if (file.size > VIDEO_MAX_SIZE_DROP) {
            this.$bvToast.toast(`O vídeo "${file.name}" excede o limite de 16MB.`, {
              title: 'Vídeo muito grande',
              variant: 'warning',
              solid: true,
            })
            return false
          }
          return true
        }
        if (DOCUMENT_MIMES_DROP.has(type)) {
          if (file.size > DOCUMENT_MAX_SIZE_DROP) {
            this.$bvToast.toast(`O documento "${file.name}" excede o limite de 100MB.`, {
              title: 'Documento muito grande',
              variant: 'warning',
              solid: true,
            })
            return false
          }
          return true
        }
        return false
      })
    },

    abrirSeletorPreviewAdicionarArquivos() {
      const el = this.$refs.inputPreviewAdicionarArquivos
      if (el) el.click()
    },

    onPreviewAdicionarArquivos(event) {
      const input = event.target
      const picked = Array.from(input.files || [])
      input.value = ''
      if (picked.length === 0) return

      const novos = this.filtrarArquivosMidiaCliente(picked)
      if (novos.length === 0) {
        this.$bvToast.toast('Apenas imagens (JPEG/PNG), vídeos (MP4/3GPP) e documentos (PDF, Word, Excel, PowerPoint, TXT) são aceitos!', {
          title: 'Formato não suportado',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const prevCount = this.previewFiles.length
      this.previewFiles = [...this.previewFiles, ...novos]
      this.previewLegendas = [...this.previewLegendas, ...novos.map(() => '')]
      this.previewActiveIndex = prevCount
    },

    // PREVIEW E CONFIRMAÇÃO
    fecharPreviewFullscreen() {
      this.revokeAllPreviewUrls(this.previewFiles)
      this.previewFullscreen = false
      this.previewFiles = []
      this.previewActiveIndex = 0
      this.previewLegendas = []
    },
    async enviarPreviewFullscreen() {
      const files = this.previewFiles
      const legendas = files.map((_, idx) => {
        const s = (this.previewLegendas[idx] != null) ? String(this.previewLegendas[idx]).trim() : ''
        return s
      })
      this.fecharPreviewFullscreen()

      if (this.cliente.is_group || this.cliente.is_private_chat) {
        for (let idx = 0; idx < files.length; idx++) {
          const arquivo = files[idx]
          const event = { target: { files: [arquivo], value: null } }
          await this.enviarMidiaChatInterno(event, legendas[idx])
        }
      } else {
        for (let idx = 0; idx < files.length; idx++) {
          const arquivo = files[idx]
          const cap = legendas[idx]
          await this.uploadAndSendFile(arquivo, cap || null)
        }
      }

      if (this.$refs.inputArquivo) this.$refs.inputArquivo.value = null
      this.scrollToBottom()
    },
    async uploadAndSendFile(arquivo, legenda) {
      const tipoMime = arquivo.type
      const isImagem = tipoMime.startsWith('image/')
      const isVideo = tipoMime.startsWith('video/')
      const isDocument = [
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ].includes(tipoMime)

      const mediaType = isDocument ? 'document' : (isImagem ? 'image' : isVideo ? 'video' : 'arquivo')
      const mensagemIdLocal = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const msg = {
        id: Date.now() + Math.random(),
        tipo: mediaType,
        remetente: 'atendente',
        hora: new Date().toISOString(),
        nome: arquivo.name,
        filename: arquivo.name,
        texto: legenda || null,
        status: 'enviando',
        url: null,
        progresso: 0,
        whatsapp_id: null,
        mensagemId: mensagemIdLocal,
        timestamp: new Date().toISOString(),
      }
      this.mensagens.push(msg)
      try {
        const { url, filename } = await enviarAnexo(arquivo, e => {
          msg.progresso = Math.round((e.loaded * 100) / e.total)
        })
        const payload = {
          ticketId: this.cliente.id,
          sender: 'atendente',
          message: legenda || null,
          to: this.cliente.cliente.whatsapp,
          accountId: this.$route.params.uuid,
          type: mediaType,
          url,
          atendenteId: this.atendenteId,
          mensagemId: mensagemIdLocal,
          ...(legenda ? { caption: legenda } : {}),
          ...(mediaType === 'document' ? { filename } : {}),
        }
        await sendMessage(payload)
        msg.url = url
        msg.filename = filename
        msg.status = 'enviado'
      } catch (e) {
        msg.status = 'erro'
      }
    },

    // UTILITÁRIOS PARA PREVIEW
    getFileIcon(file) {
      if (file.type.startsWith('image/')) return 'feather icon-image'
      if (file.type.startsWith('video/')) return 'feather icon-video'
      if (file.type === 'application/pdf') return 'feather icon-file-text'
      return 'feather icon-file'
    },

    getFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
    },
    getDocExtension(msg) {
      const src = msg.filename || msg.url || ''
      const match = src.match(/\.([a-zA-Z0-9]+)(?:\?|$)/)
      if (match) return match[1].toLowerCase()
      if (msg.tipo === 'pdf') return 'pdf'
      return 'doc'
    },
    getDocFilename(msg) {
      const src = msg.filename || msg.url || 'Documento'
      const filename = src.split('/').pop().split('?')[0]
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '')
      if (nameWithoutExt.length > 25) {
        return `${nameWithoutExt.slice(0, 25)}...`
      }
      return nameWithoutExt || 'Documento'
    },

    createPreviewUrl(file) {
      if (!file || (!file.type.startsWith('image/') && !file.type.startsWith('video/'))) return null
      if (!file._mcPreviewObjectUrl) {
        file._mcPreviewObjectUrl = URL.createObjectURL(file)
      }
      return file._mcPreviewObjectUrl
    },
    revokePreviewUrlIfAny(file) {
      if (file && file._mcPreviewObjectUrl) {
        URL.revokeObjectURL(file._mcPreviewObjectUrl)
        delete file._mcPreviewObjectUrl
      }
    },
    revokeAllPreviewUrls(files) {
      if (!files || !files.length) return
      files.forEach(f => this.revokePreviewUrlIfAny(f))
    },
    removerArquivoDoPreview(idx) {
      const files = this.previewFiles
      if (idx < 0 || idx >= files.length) return
      const removed = files[idx]
      this.revokePreviewUrlIfAny(removed)

      const newFiles = files.filter((_, i) => i !== idx)
      const newLegendas = this.previewLegendas.filter((_, i) => i !== idx)

      let newActive = this.previewActiveIndex
      if (idx < newActive) newActive -= 1
      else if (idx === newActive) newActive = Math.min(newActive, newFiles.length - 1)

      if (newFiles.length === 0) {
        this.fecharPreviewFullscreen()
        return
      }

      this.previewFiles = newFiles
      this.previewLegendas = newLegendas
      this.previewActiveIndex = Math.max(0, newActive)
    },
    _aplicarReacao(messageId, emoji, outbound = false) {
      if (messageId == null) return
      // Mensagens vêm Object.freeze-adas do parent — mantemos o override aqui em vez de mutar msg.reaction.
      this.$set(this.reactionOverrides, messageId, emoji ? { emoji, outbound } : null)
    },
    _removerReacao(messageId) {
      this._aplicarReacao(messageId, null, false)
    },
    // Retorna a reação efetiva da mensagem, considerando overrides locais.
    reacaoDe(msg) {
      if (!msg) return null
      const wid = msg.whatsapp_id
      if (wid != null && Object.prototype.hasOwnProperty.call(this.reactionOverrides, wid)) {
        return this.reactionOverrides[wid]
      }
      return msg.reaction || null
    },
    _onReactionReceived(data) {
      if (String(data.atendimento_id) !== String(this.cliente?.id)) return
      if (data.removed || !data.emoji) {
        this._removerReacao(data.message_id)
      } else {
        this._aplicarReacao(data.message_id, data.emoji, false)
      }
    },
    _onReactionSent(data) {
      if (String(data.atendimento_id) !== String(this.cliente?.id)) return
      this._aplicarReacao(data.waId, data.emoji, true)
    },
    toggleReactions(msg) {
      if (!msg || !msg.whatsapp_id) return
      this.ensureMsgUi(msg)
      const key = this.msgKey(msg)
      const ui = key != null ? this.msgUiStates[key] : null
      if (ui) ui._showReactions = !ui._showReactions
    },
    closeReactions(msg) {
      const key = this.msgKey(msg)
      if (key == null) return
      const ui = this.msgUiStates[key]
      if (ui) ui._showReactions = false
    },
    toggleTranscricaoExpandida(msg) {
      this.ensureMsgUi(msg)
      const key = this.msgKey(msg)
      const ui = key != null ? this.msgUiStates[key] : null
      if (ui) ui._transcricaoExpandida = !ui._transcricaoExpandida
    },
    async reagir(msg, emoji) {
      try {
        await reactToMessage(this.$route.params.uuid, msg.whatsapp_id, emoji)
        this.$bvToast.toast('Reação enviada', { title: 'OK', variant: 'success', solid: true })
      } catch (e) {
        const detail = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Tente novamente.'
        this.$bvToast.toast(String(detail), { title: 'Erro ao enviar reação', variant: 'danger', solid: true })
      } finally {
        this.closeReactions(msg)
      }
    },
    async removerReacao(msg) {
      try {
        await removeReactionFromMessage(this.$route.params.uuid, msg.whatsapp_id)
        this._removerReacao(msg.whatsapp_id)
        this.$bvToast.toast('Reação removida', { title: 'OK', variant: 'success', solid: true })
      } catch (e) {
        const detail = (e.response?.data?.message || e.response?.data?.error) || e.message || 'Tente novamente.'
        this.$bvToast.toast(String(detail), { title: 'Erro ao remover reação', variant: 'danger', solid: true })
      }
    },
    /**
     * Resolve o objeto citado ao enviar uma resposta: busca no histórico local ou monta a partir do banner de reply.
     * Evita exibir "Respondeu a um template" quando o ID é outgoing mas o find falhou (ex.: mensagem com reply aninhado).
     */
    mensagemReferenciadaParaEnvio(replyingTo) {
      if (!replyingTo) return null
      const lista = this.mensagens || []

      // Tentar encontrar a mensagem original no histórico local
      const refId = replyingTo.whatsapp_id || replyingTo.mensagemId
      if (refId != null) {
        const encontrada = buscarMensagemPorReferencia(lista, refId)
        if (encontrada) return encontrada
      }

      // Fallback: montar objeto a partir dos dados do banner de reply
      const { texto } = replyingTo
      const conteudo = replyingTo.conteudo != null ? replyingTo.conteudo : texto
      return {
        whatsapp_id: replyingTo.whatsapp_id || null,
        mensagemId: replyingTo.mensagemId || null,
        texto,
        conteudo,
        url: replyingTo.url,
        tipo: replyingTo.tipo,
        filename: replyingTo.filename,
        remetente: replyingTo.remetente || 'cliente',
      }
    },

    getReplyBannerPreview(rt) {
      if (!rt) return ''
      const cap = rt.texto != null && String(rt.texto).trim() !== '' ? String(rt.texto).trim() : ''
      if (rt.tipo === 'arquivo') {
        const s = cap || 'Respondeu a um arquivo'
        return s.length > 60 ? `${s.slice(0, 60)}...` : s
      }
      if (rt.tipo === 'document' || rt.tipo === 'pdf') {
        const s = cap || this.getDocFilename({ filename: rt.filename, url: rt.url, tipo: rt.tipo })
        return s.length > 60 ? `${s.slice(0, 60)}...` : s
      }
      const base = cap || rt.url || 'Mensagem'
      return base.length > 60 ? `${base.slice(0, 60)}...` : base
    },
    prepararResposta(msg) {
      // Definir a mensagem que será respondida
      // Armazenar whatsapp_id para referência e texto/url para exibição na barra
      this.replyingTo = {
        whatsapp_id: msg.whatsapp_id,
        texto: msg.texto,
        conteudo: msg.conteudo,
        url: msg.url,
        tipo: msg.tipo,
        filename: msg.filename,
        remetente: msg.remetente,
        mensagemId: msg.mensagemId,
      }
      // Focar no input de mensagem para facilitar o fluxo de digitação
      this.$nextTick(() => {
        if (this.$refs.inputMensagem) {
          this.$refs.inputMensagem.focus()
        }
      })
    },
    async handlePaste(event) {
      const items = (event.clipboardData && event.clipboardData.items) ? event.clipboardData.items : null
      if (!items) return

      const IMAGE_MIMES_PASTE = new Set(['image/jpeg', 'image/png'])
      const isClientChat = !this.cliente.is_group && !this.cliente.is_private_chat

      const pastedFiles = []
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.startsWith('image/')) {
          event.preventDefault()

          if (isClientChat && !IMAGE_MIMES_PASTE.has(item.type)) {
            this.$bvToast.toast('Formato de imagem não suportado. Use apenas JPEG ou PNG.', {
              title: 'Formato não aceito',
              variant: 'warning',
              solid: true,
            })
            continue
          }

          const file = item.getAsFile()
          if (!file) continue

          const tamanhoMax = isClientChat
            ? 5 * 1024 * 1024
            : (this.cliente.is_group || this.cliente.is_private_chat)
              ? 100 * 1024 * 1024
              : 5 * 1024 * 1024

          if (file.size > tamanhoMax) {
            const tamanhoMaxMB = isClientChat ? '5MB' : '100MB'
            this.$bvToast.toast(`A imagem colada excede o tamanho máximo de ${tamanhoMaxMB}.`, {
              title: 'Arquivo muito grande',
              variant: 'warning',
              solid: true,
            })
            continue
          }
          pastedFiles.push(file)
        }
      }
      if (pastedFiles.length > 0) {
        this.previewFiles = pastedFiles
        this.previewActiveIndex = 0
        this.previewLegendas = pastedFiles.map(() => '')
        this.previewFullscreen = true
      }
    },

    executarBuscaInline() {
      const termo = this.termoBuscaInline.toLowerCase()
      if (termo.length < 3) {
        this.resultadosBusca = []
        this.indiceResultadoAtual = -1
        return
      }
      this.resultadosBusca = this.mensagensOrdenadas
        .filter(msg => msg.texto && msg.texto.toLowerCase().includes(termo))
        .map(msg => msg.id || msg.timestamp)
      this.indiceResultadoAtual = this.resultadosBusca.length > 0 ? 0 : -1
      if (this.indiceResultadoAtual >= 0) {
        this.scrollParaResultadoBusca()
      }
    },

    highlightText(text, term) {
      if (!text || !term || term.length < 3) return text || ''
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escaped})`, 'gi')
      const safeText = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return safeText.replace(regex, '<mark>$1</mark>')
    },

    isMensagemResultadoAtual(msg) {
      if (this.indiceResultadoAtual < 0 || this.resultadosBusca.length === 0) return false
      const idAtual = this.resultadosBusca[this.indiceResultadoAtual]
      return (msg.id || msg.timestamp) === idAtual
    },

    navegarBuscaProximo() {
      if (this.resultadosBusca.length === 0) return
      this.indiceResultadoAtual = (this.indiceResultadoAtual + 1) % this.resultadosBusca.length
      this.scrollParaResultadoBusca()
    },

    navegarBuscaAnterior() {
      if (this.resultadosBusca.length === 0) return
      this.indiceResultadoAtual = this.indiceResultadoAtual <= 0
        ? this.resultadosBusca.length - 1
        : this.indiceResultadoAtual - 1
      this.scrollParaResultadoBusca()
    },

    scrollParaResultadoBusca() {
      const idAlvo = this.resultadosBusca[this.indiceResultadoAtual]
      if (!idAlvo) return

      // Expandir janela visível se a mensagem alvo não estiver renderizada
      const msgIndex = this.mensagensOrdenadas.findIndex(m => (m.id || m.timestamp) === idAlvo)
      if (msgIndex >= 0) {
        const total = this.mensagensOrdenadas.length
        const inicioAtual = Math.max(0, total - this.mensagensVisivelCount)
        if (msgIndex < inicioAtual) {
          this.mensagensVisivelCount = total - msgIndex
        }
      }

      this.$nextTick(() => {
        if (msgIndex < 0) return
        const refKey = `msg-${this.mensagensOrdenadas[msgIndex].id || msgIndex}`
        const els = this.$refs[refKey]
        const el = Array.isArray(els) ? els[0] : els
        const container = this.$refs.chatBody
        if (el && container) {
          const elTop = el.offsetTop
          const elHeight = el.offsetHeight
          const containerHeight = container.clientHeight
          const targetScrollTop = elTop - (containerHeight / 2) + (elHeight / 2)
          container.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
        }
      })
    },

    fecharBuscaInline() {
      this.buscaInlineAtiva = false
    },
  },
}
</script>

<style>
.chat-atendimento {
  background-color: #f7f9fb;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  border-top-right-radius: 0;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e000;
  z-index: 10;
  position: relative;
}

.search-inline-bar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  gap: 8px;
}

.search-inline-bar input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  padding: 4px 8px;
  background: #f5f5f5;
  border-radius: 18px;
}

.search-result-counter {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.chat-atendimento mark {
  background-color: #FFF176;
  padding: 0 2px;
  border-radius: 2px;
}

.search-result-active {
  outline: 2px solid #9A3092;
  outline-offset: 2px;
  border-radius: 12px;
}

@media (max-width: 768px) {
  .chat-header {
    z-index: 1050 !important;
    position: sticky;
    top: 0;
  }
}

/* Header com informações do contato */
.header-contato-info {
  padding: 4px 8px;
  border-radius: 8px;
  margin-left: 8px;
  margin-right: 8px;
  width: 100%;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: all 0.2s ease;
}

.header-contato-info:hover {
  background-color: rgba(115, 103, 240, 0.05);
}

.header-contato-info:active {
  background-color: rgba(115, 103, 240, 0.1);
  transform: scale(0.98);
}

.contato-nome {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.2;
  margin: 0;
  font-weight: 600;
}

/* Bolinhas das tags ao lado do nome */
.tag-dots-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tag-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.tag-dot:hover {
  transform: scale(1.3);
}

.tag-badge-header {
  font-size: 0.7rem;
  padding: 4px 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.tag-badge-header:hover {
  opacity: 0.85;
  transform: scale(1.05);
}

.tag-badge-header .tag-text {
  line-height: 1;
}

.tag-badge-header .tag-remove-icon {
  transition: transform 0.2s ease;
}

.badge-tag-header-removivel {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.badge-tag-header-removivel:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.badge-tag-header-removivel:active {
  transform: scale(0.95);
}

.campo-tags-header {
  flex-shrink: 0;
}

/* T5: Melhorar botão de tags no header */
.campo-tags-header >>> .btn {
  padding: 6px 12px;
  min-height: 32px;
}

.campo-tags-header >>> .btn:hover {
  background-color: rgba(115, 103, 240, 0.1);
}

/* T6: Tags do cliente como chips no header */
.tags-header-chips {
  margin-left: 8px;
}

.tag-chip-header {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.tag-chip-header:hover {
  filter: brightness(0.95);
}

.tag-chip-remove-header {
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.tag-chip-remove-header:hover {
  opacity: 1;
}

.tags-mais-header {
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  margin-left: 2px;
}

body.dark-layout .tags-mais-header {
  color: #b4b7bd;
}

/* Padronizar botões Tags e Ações no header */
.campo-tags-header >>> .btn,
.dropdown-acoes-container .btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  height: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  line-height: 1;
}

/* Gap entre botões Tags e Ações */
.gap-2 {
  gap: 0.5rem;
}

/* Responsividade para mobile */
@media (max-width: 768px) {
  .header-contato-info {
    margin-left: 0;
    padding: 2px 4px;
  }

  .contato-nome {
    font-size: 0.9rem;
  }

  .tag-dot {
    width: 8px;
    height: 8px;
  }

  .tag-dots-container {
    gap: 4px;
  }

  .tag-badge-header {
    font-size: 0.65rem;
    padding: 3px 6px;
    gap: 2px;
  }

  .tag-badge-header .tag-remove-icon {
    width: 10px;
    height: 10px;
  }

  .badge-tag-header-removivel {
    font-size: 0.65rem;
    padding: 2px 6px;
  }

  .badge-tag-header-removivel i {
    font-size: 10px;
  }

  /* Botões menores no mobile */
  .campo-tags-header >>> .btn,
  .dropdown-acoes-container .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    height: 28px;
    min-height: 28px;
  }
}

/* Histórico de conversas (sessões anteriores) acima da conversa atual */
.chat-historico-conversas {
  margin-bottom: 1rem;
}
.chat-historico-conversas__heading {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #5b21b6;
  margin-bottom: 0.75rem;
  letter-spacing: 0.02em;
}
.chat-historico-date-row {
  display: flex;
  align-items: center;
  margin: 0.75rem 0 0.5rem;
  gap: 0.5rem;
}
.chat-historico-date-row::before,
.chat-historico-date-row::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #d8b4fe 15%, #d8b4fe 85%, transparent);
}
.chat-historico-date-row__label {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}
.chat-historico-card {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.5rem;
  background: #fff;
  border: 1px solid #e9d5ff;
  border-radius: 10px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.chat-historico-card:hover {
  border-color: #c084fc;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.12);
}
.chat-historico-card:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.35);
}
.chat-historico-card__emoji {
  font-size: 1.25rem;
  line-height: 1;
  flex-shrink: 0;
}
.chat-historico-card__body {
  flex: 1;
  min-width: 0;
}
.chat-historico-card__status {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #111827;
  margin-bottom: 0.15rem;
}
.chat-historico-card__preview {
  font-size: 0.8125rem;
  color: #6b7280;
  max-width: 100%;
}
.chat-historico-card__time {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  padding-top: 0.1rem;
}
.chat-historico-divider-atual {
  display: flex;
  align-items: center;
  margin: 1rem 0 0.75rem;
  gap: 0.75rem;
}
.chat-historico-divider-atual::before,
.chat-historico-divider-atual::after {
  content: '';
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, transparent, #7c3aed 20%, #7c3aed 80%, transparent);
  border-radius: 1px;
}
.chat-historico-divider-atual span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5b21b6;
  white-space: nowrap;
  text-transform: none;
}

body.dark-layout .chat-historico-conversas__heading {
  color: #c4b5fd;
}
body.dark-layout .chat-historico-date-row__label {
  color: #9ca3af;
}
body.dark-layout .chat-historico-card {
  background: #25293c;
  border-color: #4c1d95;
}
body.dark-layout .chat-historico-card:hover {
  border-color: #7c3aed;
}
body.dark-layout .chat-historico-card__status {
  color: #f3f4f6;
}
body.dark-layout .chat-historico-card__preview {
  color: #9ca3af;
}
body.dark-layout .chat-historico-card__time {
  color: #6b7280;
}
body.dark-layout .chat-historico-divider-atual span {
  color: #c4b5fd;
}

/* chat-body-bg: #f5f0f5 */
.chat-body {
  background-color: #f5f0f5;
}

/* Adicionar padding-bottom quando o aviso estiver visível para evitar sobreposição */
.chat-atendimento .chat-body.chat-body-with-alert {
  padding-bottom: 2rem !important;
}

@media (max-width: 768px) {
  .chat-atendimento .chat-body.chat-body-with-alert {
    padding-bottom: 4.5rem !important;
  }
}

@media (max-width: 428px) {
  .chat-atendimento .chat-body.chat-body-with-alert {
    padding-bottom: 4.2rem !important;
  }
}

/* T15: Chat footer container */
.chat-footer {
  background-color: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* T15: Input row — horizontal layout */
.chat-input-row {
  display: flex;
  align-items: flex-end;
  padding: 8px;
  gap: 6px;
}

.chat-input-center {
  position: relative;
  flex: 1;
  min-width: 0;
}

/* T20: 24h warning banner */
.alert-24h-banner {
  background-color: #FFF8E1;
  padding: 8px 12px;
  border-bottom: 1px solid #ffe082;
}

.alert-24h-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.85rem;
}

.alert-24h-icon {
  color: #f9a825;
  flex-shrink: 0;
}

.alert-24h-text {
  color: #5d4037;
  font-weight: 500;
}

.alert-24h-btn {
  background-color: #9A3092;
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 4px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s;
}

.alert-24h-btn:hover {
  background-color: #7b2574;
}

/* T20: Bot overlay banner */
.bot-overlay-banner {
  background: rgba(108, 117, 125, 0.85);
  color: #fff;
  text-align: center;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 0;
  position: relative;
  z-index: 50;
}

/* T20: fade overlay transition */
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.2s ease;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

@media (max-width: 428px) {
  .alert-24h-content {
    flex-wrap: wrap;
    font-size: 0.8rem;
  }
}

/* --- T4/T5/T6: Base bubble styles --- */
.chat-bubble {
  display: inline-block;
  max-width: 65%;
  padding: 6px 9px 8px 9px;
  border-radius: 12px;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow: none;
  position: relative;
}

@media (max-width: 768px) {
  .chat-bubble {
    max-width: 85%;
  }
}

/* Sent bubble (atendente) — WhatsApp style */
.bubble-atendente {
  background-color: #9a30912d;
  color: #333;
  border-radius: 7.5px 0 7.5px 7.5px;
}

.bubble-atendente::after {
  content: '';
  position: absolute;
  top: 0;
  right: -8px;
  width: 8px;
  height: 13px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%239a30912d' d='M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z'/%3E%3C/svg%3E");
  background-size: 8px 13px;
  background-repeat: no-repeat;
}

/* Received bubble (cliente) — WhatsApp style */
.bubble-cliente {
  background-color: #ffffff;
  color: #333;
  border-radius: 0 7.5px 7.5px 7.5px;
}

.bubble-cliente::before {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 8px;
  height: 13px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 13'%3E%3Cpath fill='%23ffffff' d='M2.812,0H8v11.193L1.533,2.568C0.474,1.156,1.042,0,2.812,0z'/%3E%3C/svg%3E");
  background-size: 8px 13px;
  background-repeat: no-repeat;
}

/* Nome do atendente em chat de grupo */
.atendente-meta {
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.atendente-meta strong {
  font-weight: 600;
  color: #5e72e4;
}

/* T6: Bot bubble — chat-bubble-bot: #fff8e1 */
.bubble-bot {
  background-color: #fff8e1;
  color: #333;
  border-left: 3px solid #FFA000;
  border-radius: 12px;
}

/* T6: No tail for bot */
.bubble-bot::before,
.bubble-bot::after {
  display: none;
}

/* T6: Bot label inside bubble */
.bot-meta-inline {
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 2px;
}

/* Estilo WhatsApp: emoji único ou poucos emojis aparecem maiores */
.only-emoji {
  font-size: 3.5rem;
  line-height: 1.2;
}

/* Quando a mensagem é só emoji, remover background do bubble */
.chat-bubble:has(.only-emoji) {
  background: transparent !important;
  box-shadow: none !important;
  padding: 2px 4px !important;
  white-space: nowrap;
  max-width: none;
}

.chat-bubble:has(.only-emoji)::before,
.chat-bubble:has(.only-emoji)::after {
  display: none !important;
}

/* Timestamp com background quando é só emoji (estilo WhatsApp) */
.chat-bubble:has(.only-emoji) .msg-timestamp-inline {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
}

.chat-bubble:has(.only-emoji) .msg-timestamp-content {
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 0.7rem;
}

.chat-bubble:has(.only-emoji) .msg-timestamp-content .msg-status-icon {
  color: #fff;
}

.emoji-picker-container {
  position: absolute;
  bottom: 100%;
  right: 50px;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.msg-avatar {
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #ddd;
}

/* T15: Emoji standalone button */
.btn-emoji-standalone {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s, background-color 0.2s;
  font-size: 1rem;
  align-self: flex-end;
  margin-bottom: 4px;
}

.btn-emoji-standalone:hover {
  color: #9A3092;
  background-color: rgba(154, 48, 146, 0.08);
}

/* T16: Send / Mic toggle button container */
.btn-send-mic {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  position: relative;
  align-self: flex-end;
}

.btn-send-mic-inner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: #9A3092;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s, background-color 0.2s;
}

.btn-send-mic-inner:hover {
  background-color: #7b2574;
}

.btn-send-mic-inner:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* T16: Send/Mic swap transitions */
.send-mic-enter-active,
.send-mic-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.send-mic-enter {
  transform: scale(0.6);
  opacity: 0;
}

.send-mic-leave-to {
  transform: scale(0.6);
  opacity: 0;
}

/* Legacy audio-button kept for recording bar usage */
.audio-button {
  background-color: transparent;
  border: none;
  color: #555;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 1.1rem;
  box-shadow: none;
}

.audio-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.audio-button.recording {
  background-color: #ff4d4d;
  color: white;
  animation: pulse-wave 1s infinite;
}

@keyframes pulse-wave {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.6; }
}

.animate-fade-in {
  animation: fadeInScale 0.3s ease-out;
}

@keyframes fadeInScale {
  0% { opacity: 0; transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
}

/* Transições suaves */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Animações de entrada - apenas para novas mensagens, não para histórico */
.fade-bubble-enter-active {
  transition: all 0.2s ease-out;
}
.fade-bubble-leave-active {
  transition: all 0.15s ease-in;
}
.fade-bubble-enter {
  opacity: 0;
  transform: translateY(15px);
}
.fade-bubble-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mensagens enviadas pelo atendente entram da direita */
.fade-bubble-enter.justify-content-end {
  transform: translateX(20px);
}

/* Typing indicator */
.typing-indicator {
  display: flex;
  justify-content: flex-start;
  padding: 4px 8px;
  margin-bottom: 8px;
}

.typing-bubble {
  background: #f0f0f0;
  border-radius: 18px;
  padding: 12px 16px;
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-dot {
  width: 8px;
  height: 8px;
  background: #999;
  border-radius: 50%;
  animation: typingBounce 1.4s infinite ease-in-out;
}

.typing-dot:nth-child(1) { animation-delay: 0s; }
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.fade-typing-enter-active, .fade-typing-leave-active {
  transition: all 0.2s ease;
}
.fade-typing-enter, .fade-typing-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Botão Scroll to Bottom - Estilo WhatsApp */
.scroll-to-bottom-container {
  position: absolute;
  bottom: 80px;
  right: 20px;
  z-index: 1000;
}

.scroll-to-bottom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(11, 20, 26, 0.2);
  transition: all 0.2s ease;
  outline: none;
}

.scroll-to-bottom-btn:hover {
  background-color: #f0f2f5;
  box-shadow: 0 2px 6px rgba(11, 20, 26, 0.3);
}

.scroll-to-bottom-btn:active {
  transform: scale(0.95);
}

.scroll-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #54656f;
}

.scroll-to-bottom-btn svg {
  fill: none;
}

.scroll-to-bottom-btn svg path {
  fill: currentColor;
}

/* Ajustes para mobile */
@media (max-width: 768px) {
  .scroll-to-bottom-container {
    position: fixed;
    bottom: 160px;
    right: 16px;
    z-index: 1001;
  }
}

@media (max-width: 480px) {
  .scroll-to-bottom-container {
    position: fixed;
    bottom: 140px;
    right: 8px;
  }
}

.custom-audio-player {
  background-color: #f1f3f5;
  border: 1px solid #dcdfe3;
  border-radius: 8px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  font-size: 0.9rem;
}

.lilac-progress .progress-bar {
  background-color: #b074f0 !important;
}

/* T9: Image messages in bubbles — estilo WhatsApp */
.chat-bubble:has(.img-bubble-wrapper) {
  padding: 3px !important;
  overflow: hidden;
  max-width: min(330px, 70vw);
  width: fit-content;
}

@media (max-width: 768px) {
  .chat-bubble:has(.img-bubble-wrapper) {
    max-width: min(80vw, 330px);
  }
}

.img-bubble-wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
}

.img-bubble-container {
  position: relative;
  cursor: pointer;
  display: block;
  line-height: 0;
}

.img-msg-preview {
  width: 100%;
  max-height: 400px;
  display: block;
  border-radius: 0;
}

/* Timestamp overlay sobre a imagem (sem legenda) */
.img-timestamp-overlay {
  position: absolute;
  bottom: 6px;
  right: 7px;
  pointer-events: none;
}

.img-timestamp-content {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(0, 0, 0, 0.42);
  color: #fff;
  border-radius: 7px;
  padding: 1px 5px;
  font-size: 0.72rem;
  line-height: 1.4;
  white-space: nowrap;
}

.img-timestamp-content .msg-status-icon {
  color: rgba(255,255,255,0.85);
}

/* Legenda abaixo da imagem */
.img-caption {
  padding: 5px 9px 6px 9px;
  font-size: 0.9rem;
  line-height: 1.4;
}

.doc-caption {
  padding: 4px 9px 6px 9px;
  font-size: 0.85rem;
  line-height: 1.4;
  color: inherit;
  opacity: 0.9;
  word-break: break-word;
}

.chat-atendimento {
  min-width: 0;
}

/* T7: message-text — no duplicate padding (bubble handles it) */
.message-text {
  display: block;
  padding: 0;
  line-height: 1.4;
  max-width: 100%;
  word-break: break-word;
  white-space: pre-wrap;
  position: relative;
}

/* T7: Inline timestamp inside bubble */
.msg-timestamp-inline {
  display: inline;
  position: relative;
}

.msg-timestamp-spacer {
  display: inline-block;
  width: 2px;
  height: 0;
  vertical-align: baseline;
}

.msg-timestamp-content {
  display: flex;
  align-items: center;
  gap: 3px;
  float: right;
  font-size: 11px;
  /* chat-check-delivered: rgba(0,0,0,0.45) */
  color: rgba(0, 0, 0, 0.45);
  margin-top: 4px;
  margin-left: 8px;
  white-space: nowrap;
}

.msg-status-icon {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
}
/* T19: Reply inline banner */
.reply-banner {
  display: flex;
  align-items: center;
  background: #f1f3f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  gap: 8px;
  overflow: hidden;
}

.reply-banner-bar {
  width: 3px;
  min-height: 32px;
  background-color: #9A3092;
  border-radius: 2px;
  flex-shrink: 0;
}

.reply-banner-body {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.reply-banner-sender {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9A3092;
  line-height: 1.2;
}

.reply-banner-text {
  font-size: 0.8rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.reply-banner-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: background-color 0.15s, color 0.15s;
}

.reply-banner-close:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: #333;
}

/* T19: Reply slide transition */
.reply-slide-enter-active {
  transition: all 0.15s ease-out;
}
.reply-slide-leave-active {
  transition: all 0.1s ease-in;
}
.reply-slide-enter,
.reply-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
  margin-bottom: 0;
  padding: 0 8px;
}

@media (max-width: 576px) {
  .reply-banner {
    padding: 4px 6px;
    gap: 6px;
  }
}

/* Copiloto: posição via :style (fixed) para não ser cortado por .content-area-wrapper { overflow: hidden } */
.copiloto-dropdown {
  max-height: min(280px, 40vh);
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  background: #fff;
}

.copiloto-dropdown-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 12px;
}

.copiloto-header-sub {
  font-size: 0.65rem;
  opacity: 0.85;
  margin-top: 2px;
}

.copiloto-tone-badge {
  font-weight: 600;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copiloto-sugestao-item {
  cursor: pointer;
  padding: 10px 12px;
  transition: background-color 0.15s ease;
  border-left: 3px solid transparent;
}

.copiloto-sugestao-item:hover {
  background-color: #f1f5f9;
  border-left-color: #9a3091;
}

.copiloto-sugestao-item .suggestion-text {
  font-size: 0.82rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.8em;
  color: #334155;
  transition: all 0.2s ease;
}

.copiloto-sugestao-item .suggestion-text.expanded {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  max-height: none;
  overflow: visible;
}

.copiloto-sugestao-item .suggestion-expand {
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}
.badge-tag {
  border-radius: 999px;
  font-size: 0.75rem;
  padding: 6px 10px;
}

.remove-tag {
  background-color: rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  width: 16px;
  height: 16px;
  line-height: 1;
  text-align: center;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.remove-tag:hover {
  background-color: rgba(255, 255, 255, 0.6);
}

/* bot-meta replaced by bot-meta-inline (in main bubble styles above) */
.bot-meta {
  display: none;
}

/* bot-timestamp removed — replaced by inline .msg-timestamp-content */
.bot-timestamp {
  display: none;
}

.video-thumbnail {
  position: relative;
  cursor: pointer;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  max-width: 250px;
}

@media (max-width: 768px) {
  .video-thumbnail {
    max-width: min(80vw, 350px);
  }
}

.video-thumbnail video {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
}
.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0,0,0,0.4);
  color: #fff;
  width: 100%;
  height: 100%;
  font-size: 1rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* T18: Auto-expanding textarea */
.input-mensagem {
  resize: none;
  border-radius: 18px;
  padding: 9px 14px;
  height: 40px;
  min-height: 40px;
  max-height: 120px;
  overflow-y: hidden;
  line-height: 1.4;
  font-size: 0.9rem;
  transition: height 0.1s ease;
  border: 1px solid #e0e0e0;
}

.input-mensagem.with-magic-wand {
  padding-right: 35px; /* Espaço para a varinha mágica */
}

.input-with-magic {
  position: relative;
}

.magic-wand-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  background: none;
  border: none;
  color: #9a3091;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.magic-wand-btn:hover {
  background-color: rgba(115, 103, 240, 0.15);
  color: #9a3091;
  transform: scale(1.05);
}

.magic-wand-btn:active {
  transform: scale(0.95);
}

.magic-wand-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.magic-wand-btn:disabled:hover {
  background-color: transparent;
  transform: none;
}

.atalhos-dropdown {
  bottom: calc(100% + 4px);
  left: 0;
  max-height: 220px;
  overflow-y: auto;
  z-index: 1055;
  border-radius: 8px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
}

.atalhos-dropdown .list-group-item {
  padding: 8px 12px;
  cursor: pointer;
  border-left: none;
  border-right: none;
}

.atalhos-dropdown .list-group-item:first-child {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-top: none;
}

.atalhos-dropdown .list-group-item:last-child {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.atalhos-dropdown .list-group-item.active {
  background-color: #9a3091;
  border-color: #9a3091;
  color: #fff;
}

.atalhos-dropdown .list-group-item.active small {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Animação de rotação para o SVG */
.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Estilo para o botão chevron */
.chevron-btn {
  border: 1px solid #dee2e6 !important;
  background-color: #f8f9fa !important;
  border-radius: 4px !important;
}

.chevron-btn:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

/* Controles do Copiloto - estilo discreto */
.copiloto-controls {
  margin-top: 2px !important;
  border: none !important;
  border-top: none !important;
}

.copiloto-discrete-btn {
  background: none !important;
  border: none !important;
  text-decoration: none !important;
  color: #adb5bd !important;
  font-size: 10px !important;
  transition: all 0.2s ease;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1 !important;
}

.copiloto-discrete-btn small {
  color: #adb5bd !important;
  font-size: 10px !important;
}

.copiloto-discrete-btn:hover {
  color: #6c757d !important;
  text-decoration: none !important;
  background-color: rgba(0,0,0,0.03) !important;
  border-radius: 3px;
  padding: 1px 3px !important;
}

.copiloto-discrete-btn:hover small {
  color: #6c757d !important;
}

.copiloto-discrete-btn:focus {
  box-shadow: none !important;
  outline: none !important;
}

.copiloto-discrete-btn i {
  display: inline-block !important;
  font-size: 8px !important;
  margin-left: 3px !important;
  vertical-align: middle !important;
  color: #adb5bd !important;
}

.copiloto-discrete-btn:hover i {
  color: #6c757d !important;
}

/* Botões de fechar */
.close-suggestions-btn {
  background-color: #ffffff !important;
  border: 2px solid #dee2e6 !important;
  border-radius: 50% !important;
  width: 28px !important;
  height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
  z-index: 1000 !important;
  position: relative !important;
}

.close-suggestions-btn:hover {
  background-color: #f8f9fa !important;
  border-color: #dc3545 !important;
  color: #dc3545 !important;
  transform: scale(1.1) !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
}

.close-suggestions-btn span {
  color: #6c757d !important;
  font-size: 16px !important;
  font-weight: bold !important;
  line-height: 1 !important;
}

.close-suggestions-btn:hover span {
  color: #dc3545 !important;
}

.close-collapsible-btn {
  background-color: #ffffff !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 50% !important;
  width: 22px !important;
  height: 22px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
  z-index: 10 !important;
}

.close-collapsible-btn:hover {
  background-color: #f8f9fa !important;
  border-color: #dc3545 !important;
  color: #dc3545 !important;
  transform: scale(1.05) !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15) !important;
}

.close-collapsible-btn span {
  color: #6c757d !important;
  font-size: 14px !important;
  font-weight: bold !important;
  line-height: 1 !important;
}

.close-collapsible-btn:hover span {
  color: #dc3545 !important;
}

/* ── Audio Player (estilo WhatsApp) ── */
.audio-player-ui {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 6px;
  border-radius: 12px;
  background-color: transparent;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
}

/* Botão play circular */
.btn-audio-play {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background-color: #9A3092;
  color: #fff;
  transition: background-color 0.15s;
  padding: 0;
}

.btn-audio-play:hover {
  background-color: #7b2576;
}

.bubble-atendente .btn-audio-play {
  background-color: #9a3091c9;
}

.bubble-atendente .btn-audio-play:hover {
  background-color: #75256fc9;
}

/* Coluna central */
.audio-center {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.audio-waveform {
  display: flex;
  align-items: center;
  gap: 1.5px;
  height: 24px;
  cursor: pointer;
  overflow: hidden;
}

.audio-waveform .waveform-bar {
  width: 2.5px;
  min-width: 1px;
  border-radius: 1.5px;
  transition: background-color 0.2s ease-in-out;
  flex-shrink: 1;
  background-color: rgb(188, 169, 187);
}

.audio-waveform .waveform-bar--played {
  background-color: #9A3092;
}

.bubble-atendente .audio-waveform .waveform-bar {
  background-color: rgba(0, 0, 0, 0.18);
}

.bubble-atendente .audio-waveform .waveform-bar--played {
  background-color: #9a3091c9;
}

/* Linha de info: tempo + velocidade */
.audio-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.audio-time {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}

.btn-audio-speed {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  padding: 0;
  line-height: 1;
}

.btn-audio-speed:hover {
  color: rgba(0, 0, 0, 0.75);
}

.bubble-atendente .audio-time,
.bubble-atendente .btn-audio-speed {
  color: rgba(0, 0, 0, 0.4);
}

/* Avatar com badge de microfone */
.audio-avatar-wrap {
  flex-shrink: 0;
  position: relative;
  width: 40px;
  height: 40px;
}

.audio-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #9a3091c9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.bubble-atendente .audio-avatar {
  background: #9a3091c9;
}

.audio-mic-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #525252;
}

/* Audio bubble */
.bubble-audio {
  display: inline-flex;
  padding: 4px;
  border-radius: 10px;
  box-shadow: none;
  max-width: 300px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .bubble-audio {
    max-width: 230px;
  }
}

.bubble-audio.bubble-cliente {
  background-color: #ffffff;
}

.bubble-audio.bubble-atendente {
  background-color: #E5CEE4;
}

.transcrever-link {
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;
}

.transcrever-link:hover .transcrever-text {
  text-decoration: underline;
}

.transcricao-audio {
  background-color: #f8f9fa;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #333;
  white-space: pre-wrap;
  line-height: 1.4;
  max-height: 7.5em; /* aproximadamente 5 linhas */
  overflow: hidden;
  transition: max-height 0.3s ease;
  position: relative;
  max-height: 7.5em;
  overflow: hidden;
}

.transcricao-audio.expandida {
  max-height: none;
  overflow: visible;
}

.toggle-transcricao {
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;
}
.toggle-transcricao:hover {
  text-decoration: underline;
}

/* Botão voltar - apenas visível no mobile */
.btn-voltar-mobile {
  display: none;
}

@media (max-width: 768px) {
  .btn-voltar-mobile {
    display: inline-flex !important;
    z-index: 1051 !important;
    position: relative;
  }
}

/* Dropdown de Ações */
.dropdown-acoes-container {
  position: relative;
  display: inline-block;
}

.dropdown-menu-acoes {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 220px;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  background-color: #fff;
  z-index: 500;
  display: none;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.2s ease;
}

.dropdown-menu-acoes.show {
  display: block;
  opacity: 1;
  transform: translateY(0);
}

.dropdown-menu-acoes .dropdown-item {
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #495057;
  text-decoration: none;
}

.dropdown-menu-acoes .dropdown-item:hover {
  background-color: #f8f9fa;
  transform: translateX(2px);
  color: #495057;
  text-decoration: none;
}

.dropdown-menu-acoes .dropdown-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.dropdown-menu-acoes .dropdown-item.disabled:hover {
  background-color: transparent;
  transform: none;
}

.dropdown-menu-acoes .dropdown-item i {
  width: 18px;
  height: 18px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropdown-menu-acoes .dropdown-header {
  padding: 0.5rem 1rem 0.25rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.dropdown-menu-acoes .dropdown-divider {
  margin: 0.5rem 0;
  border-top: 1px solid #e9ecef;
}

/* Responsividade do dropdown */
@media (max-width: 768px) {
  .dropdown-menu-acoes {
    min-width: 200px;
    font-size: 0.8rem;
  }

  .dropdown-menu-acoes .dropdown-item {
    padding: 0.625rem 0.875rem;
  }

  .dropdown-menu-acoes .dropdown-item i {
    width: 16px;
    height: 16px;
    font-size: 14px;
  }

  .dropdown-menu-acoes .dropdown-header {
    font-size: 0.7rem;
    padding: 0.375rem 0.875rem 0.125rem 0.875rem;
  }
}

@media (max-width: 375px) {
  .dropdown-menu-acoes {
    min-width: 180px;
    font-size: 0.75rem;
  }

  .dropdown-menu-acoes .dropdown-item {
    padding: 0.5rem 0.75rem;
  }

  .dropdown-menu-acoes .dropdown-item i {
    width: 14px;
    height: 14px;
    font-size: 12px;
  }
}

/* Animação da seta do dropdown */
.feather.icon-chevron-down {
  transition: transform 0.2s ease;
}

.feather.icon-chevron-down.rotated {
  transform: rotate(180deg);
}

/* Avatar wrapper e contato no header - apenas visível no mobile */
.avatar-wrapper {
  display: none;
}

@media (max-width: 768px) {
  .avatar-wrapper {
    display: inline-flex !important;
    cursor: pointer !important;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    pointer-events: auto !important;
    z-index: 9999 !important;
    position: relative !important;
    padding: 4px !important; /* Área de clique maior */
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .avatar-wrapper:hover,
  .avatar-wrapper:active {
    background-color: rgba(0, 123, 255, 0.1);
    transform: scale(1.05);
  }

  .avatar-contato {
    display: inline-flex !important;
    width: 60px !important;
    height: 60px !important;
    font-size: 20px !important;
    border: 2px solid #e9ecef;
    transition: all 0.2s ease;
    pointer-events: none !important; /* O wrapper controla o clique */
  }

  .avatar-wrapper:hover .avatar-contato,
  .avatar-wrapper:active .avatar-contato {
    border-color: #007bff !important;
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
  }
}

@media (max-width: 375px) {
  .avatar-contato {
    width: 40px !important;
    height: 40px !important;
    font-size: 16px !important;
    border: 1px solid #e9ecef;
  }
}

/* T15/T17: Attachment "+" button and popup */
.dropdown-anexos-container {
  position: relative;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 4px;
}

.btn-attach-plus {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
  padding: 0;
  font-size: 1rem;
  color: #555;
}

.btn-attach-plus:hover {
  background-color: #e9ecef;
  transform: rotate(45deg);
}

.btn-carrinho {
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 4px;
}

.btn-carrinho:hover {
  background-color: #d4edda;
  color: #28a745;
  transform: none;
}

.btn-attach-plus:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

/* T17: Attachment popup */
.attach-popup {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.attach-popup-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.attach-popup-item:hover {
  background-color: #f5f5f5;
}

.attach-popup-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
}

.attach-popup-icon--blue { background-color: #2196F3; }
.attach-popup-icon--purple { background-color: #9A3092; }
.attach-popup-icon--green { background-color: #4CAF50; }

.attach-popup-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}

/* T17: Popup animation */
.popup-menu-enter-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.popup-menu-leave-active {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}
.popup-menu-enter {
  transform: scale(0.8);
  opacity: 0;
}
.popup-menu-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

/* Remover padding do app-content no mobile para o chat */
@media (max-width: 575.98px) {
  html[dir] body .app-content {
    padding: 0 !important;
  }
}

/* Responsividade do popup de anexos */
@media (max-width: 768px) {
  .btn-attach-plus {
    width: 28px;
    height: 28px;
  }

  .attach-popup {
    min-width: 160px;
  }

  .attach-popup-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .attach-popup-label {
    font-size: 0.8rem;
  }
}

@media (max-width: 375px) {
  .attach-popup {
    min-width: 140px;
  }
}

/* CORREÇÃO: CHAT-FOOTER VISÍVEL EM 428PX E MENORES */
@media (max-width: 428px) {
  .chat-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: #fff;
    display: flex;
    visibility: visible;
    opacity: 1;
    width: 100%;
    min-height: 56px;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  }

  .chat-atendimento {
    overflow: visible;
    height: 100vh;
    padding-bottom: 70px;
  }

  .chat-body {
    padding-bottom: 80px;
    max-height: calc(100vh - 140px);
    overflow-y: auto;
  }

  .chat-atendimento .chat-body.chat-body-with-alert {
    padding-bottom: 120px;
  }
}

/* CORREÇÃO: CHAT USAR 100% DA LARGURA EM 430PX PARA CIMA */
@media (min-width: 430px) {
  .chat-atendimento {
    width: 100% !important;
    max-width: 100% !important;
  }

  .chat-header,
  .chat-body,
  .chat-footer {
    width: 100% !important;
    max-width: 100% !important;
  }
}

/* Drag and Drop Overlay */
.drag-overlay {
  position: absolute;
  inset: 0;
  background: rgba(154, 48, 146, 0.08);
  border: 3px dashed #9A3092;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
}

.drag-content {
  text-align: center;
  color: #007bff;
}

/* Modal de Preview de Arquivos */
.drag-preview-container {
  max-height: 60vh;
  overflow-y: auto;
}

.preview-files {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.preview-file-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s ease;
}

.preview-file-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.preview-content {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.preview-img,
.preview-vid {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.file-info {
  width: 100%;
  text-align: center;
}

.file-name {
  font-size: 0.85rem;
  font-weight: 500;
  word-break: break-word;
  margin-bottom: 0.25rem;
}

.file-size {
  font-size: 0.75rem;
}

/* T13: Quoted messages — estilo WhatsApp */
.quoted-message {
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  max-height: 100%;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: rgba(0, 0, 0, 0.05);
  border-left: 4px solid #9a30912d;
  border-radius: 6px;
  font-size: 0.85rem;
  overflow: hidden;
}

.quoted-message:hover {
  background: rgba(0, 0, 0, 0.09);
}

/* Bolha com reply: largura mínima para não cortar o reply */
.chat-bubble:has(.quoted-message) {
  min-width: 200px;
}

/* Bolha com documento: largura mínima para exibir corretamente */
.chat-bubble:has(.doc-card) {
  min-width: 220px;
}

.quoted-left {
  border-left-color: #9a3092;
}

.quoted-right {
  border-left-color: #9A3092;
}

/* Container flex: corpo à esquerda, thumbnail à direita */
.quoted-inner {
  display: flex;
  align-items: stretch;
  min-height: 52px;
  min-width: 0;
  max-width: 100%;
}

/* Corpo de texto (nome + ícone + legenda) */
.quoted-body {
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.quoted-sender {
  font-weight: 600;
  color: #9A3092;
  font-size: 0.82rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Linha com ícone SVG + legenda */
.quoted-media-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #495057;
  min-width: 0;
  flex: 1;
}

.quoted-media-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

.quoted-media-caption {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.82rem;
  min-width: 0;
  flex: 1;
}

.quoted-media-caption--placeholder {
  color: #888;
  font-style: italic;
}

/* Thumbnail à direita como background-image */
.quoted-thumbnail-right {
  width: 60px;
  min-width: 60px;
  background-size: cover;
  background-position: center;
  background-color: rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quoted-thumbnail-video {
  overflow: hidden;
}

.quoted-video-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.quoted-video-play {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  position: relative;
  z-index: 1;
}

.quoted-text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #495057;
  font-size: 0.82rem;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* T11: Stickers without bubble background */
.bubble-sticker {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 4px;
}

.bubble-sticker::before,
.bubble-sticker::after {
  display: none;
}

.sticker-img {
  max-width: 150px;
}

/* T12: Document/PDF compact card — WhatsApp style */
.doc-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  max-width: 100%;
  box-sizing: border-box;
}

.doc-card:hover {
  background: rgba(0, 0, 0, 0.08);
}

.doc-card-icon {
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.doc-icon--pdf { color: #E53935; }
.doc-icon--doc, .doc-icon--docx { color: #2196F3; }
.doc-icon--xls, .doc-icon--xlsx { color: #4CAF50; }
.doc-icon--ppt, .doc-icon--pptx { color: #FF9800; }
.doc-icon--txt { color: #607D8B; }
.doc-icon--zip, .doc-icon--rar { color: #9C27B0; }

.doc-icon-badge {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 7px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.doc-card-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.doc-card-name {
  font-size: 0.875rem;
  font-weight: 400;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doc-card-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(17, 27, 33, 0.5);
}

.doc-meta-separator {
  color: rgba(17, 27, 33, 0.35);
}

/* Video bubble with play overlay */
.video-bubble-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  max-width: 250px;
}

@media (max-width: 768px) {
  .video-bubble-container {
    max-width: min(80vw, 350px);
  }
}

.video-bubble-thumb {
  display: block;
  max-width: 250px;
  width: 100%;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .video-bubble-thumb {
    max-width: min(80vw, 350px);
  }
}

.video-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.15);
  transition: background 0.2s;
}

.video-bubble-container:hover .video-play-overlay {
  background: rgba(0, 0, 0, 0.3);
}

.video-play-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 3px;
}

/* Fullscreen file preview overlay */
.file-preview-overlay {
  position: absolute;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
}

.preview-close-btn {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.preview-close-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.preview-main-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 60px 20px 20px;
}

.preview-main-img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
}

.preview-main-video {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
}

.preview-main-doc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #fff;
}

.preview-doc-name {
  font-size: 1rem;
  font-weight: 500;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-doc-size {
  font-size: 0.8rem;
  opacity: 0.7;
}

.preview-bottom-bar {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-thumbnails {
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 8px 0;
}

.preview-thumb {
  position: relative;
  width: 75px;
  height: 75px;
  border-radius: 6px;
  overflow: visible;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: border-color 0.2s;
  flex-shrink: 0;
}

.preview-thumb-inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.preview-thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  transition: background 0.15s, transform 0.15s;
}

.preview-thumb-remove:hover {
  background: #c0392b;
  transform: scale(1.05);
}

.preview-thumb-remove:focus {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

.preview-thumb--active {
  border-color: #9A3092;
}

.preview-thumb--add {
  cursor: pointer;
  border: 2px dashed rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.preview-thumb--add:hover {
  border-color: #9A3092;
  background: rgba(154, 48, 146, 0.25);
  color: #fff;
}

.preview-thumb--add:focus {
  outline: 2px solid #9A3092;
  outline-offset: 2px;
}

.preview-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-caption-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.preview-caption-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  color: #fff;
  font-size: 0.9rem;
  resize: none;
  outline: none;
}

.preview-caption-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.preview-send-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #9A3092;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
}

.preview-send-btn:hover {
  background: #832980;
}

/* Mobile adjustments for file preview overlay */
@media (max-width: 768px) {
  .preview-main-area {
    padding: 50px 12px 12px;
  }

  .preview-main-img,
  .preview-main-video {
    max-height: 55vh;
  }

  .preview-close-btn {
    top: 12px;
    right: 12px;
    left: auto;
    width: 40px;
    height: 40px;
    background: rgba(0, 0, 0, 0.6);
  }

  .preview-bottom-bar {
    padding: 8px 12px 12px;
    background: rgba(0, 0, 0, 0.3);
  }

  .preview-caption-input {
    font-size: 0.85rem;
    padding: 8px 12px;
    min-height: 36px;
  }

  .preview-send-btn {
    width: 44px;
    height: 44px;
  }

  .preview-thumb {
    width: 40px;
    height: 40px;
  }

  .preview-doc-name {
    font-size: 0.9rem;
    max-width: 250px;
  }
}

/* T14: Floating action buttons on hover */
.mensagem-item {
  position: relative;
}

.reactions-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
  align-self: center;
  margin: 0 4px;
}

.mensagem-item:hover .reactions-wrapper {
  opacity: 1;
}

@media (max-width: 768px) {
  .reactions-wrapper {
    opacity: 1;
  }
}

.btn-reaction {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0;
}

.btn-reaction:hover {
  background: #f5f0f5;
  border-color: #9A3092;
  transform: scale(1.1);
}

.btn-reply {
  color: #8696a0;
}

.btn-reply:hover {
  color: #9A3092;
}

.reactions-picker {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 6px;
  display: flex;
  gap: 2px;
  z-index: 100;
  white-space: nowrap;
  margin-bottom: 4px;
}

/* Estilo para mensagem destacada ao fazer scroll */
.mensagem-destacada {
  animation: highlightMessage 2s ease-in-out;
  border-radius: 8px;
  padding: 4px;
  margin: -4px;
}

@keyframes highlightMessage {
  0% {
    background-color: rgba(154, 48, 146, 0.3);
    box-shadow: 0 0 12px rgba(154, 48, 146, 0.4);
  }
  25% {
    background-color: rgba(154, 48, 146, 0.15);
    box-shadow: 0 0 6px rgba(154, 48, 146, 0.2);
  }
  50% {
    background-color: rgba(154, 48, 146, 0.25);
    box-shadow: 0 0 10px rgba(154, 48, 146, 0.35);
  }
  75% {
    background-color: rgba(154, 48, 146, 0.1);
  }
  100% {
    background-color: transparent;
    box-shadow: none;
  }
}

/* T31-T32: Waveform recording bar */
.audio-recording-bar {
  padding: 6px 10px;
  gap: 8px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.waveform-container {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  height: 32px;
  padding: 0 12px;
}

.waveform-container .waveform-bar {
  width: 3px;
  min-height: 4px;
  background-color: #9A3092;
  border-radius: 2px;
  transition: height 0.05s ease;
}

.recording-timer {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #333;
  min-width: 45px;
  text-align: center;
  margin: 0 8px;
}

.btn-recording-action {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-cancel {
  background: #fee;
  color: #dc3545;
}
.btn-cancel:hover {
  background: #fdd;
}

.btn-stop {
  background: #9A3092;
  color: #fff;
}
.btn-stop:hover {
  background: #7a2572;
}

/* Slide-up transition */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* ========== T36: Global responsive adjustments ========== */

/* T36: Search bar — tighter padding on mobile */
@media (max-width: 768px) {
  .search-inline-bar {
    padding: 6px 8px;
    gap: 4px;
  }
  .search-inline-bar input {
    font-size: 13px;
  }
}

/* T36: Emoji picker — full-width on mobile, capped height */
@media (max-width: 768px) {
  .emoji-picker-container {
    right: 0;
    left: 0;
    margin: 0 8px 8px;
    max-height: 50vh;
    overflow-y: auto;
  }
}

/* T36: Audio recording bar — full-width on mobile */
@media (max-width: 768px) {
  .audio-recording-bar {
    width: 100%;
    padding: 6px 8px;
  }
}

/* T36: Document cards — respect bubble max-width on mobile */
@media (max-width: 768px) {
  .doc-card {
    min-width: 0;
    max-width: 100%;
  }
}

/* T36: Reply banner — full-width on mobile */
@media (max-width: 768px) {
  .reply-banner {
    margin-left: 0;
    margin-right: 0;
  }
}

/* T36: 24h warning and bot overlay — compact padding on mobile */
@media (max-width: 768px) {
  .alert-24h-banner {
    padding: 6px 8px;
  }
  .bot-overlay-banner {
    padding: 8px 12px;
    font-size: 0.85rem;
  }
}

/* T36: Quoted messages — smaller thumbnail on tiny screens */
@media (max-width: 375px) {
  .quoted-thumbnail-right {
    width: 44px;
    min-width: 44px;
  }
}

/* T36: Input buttons — smaller on ≤375px */
@media (max-width: 375px) {
  .btn-attach-plus,
  .btn-emoji-standalone {
    width: 28px;
    height: 28px;
  }
}

/* Badge de reação */
.reaction-badge {
  display: inline-block;
  font-size: 16px;
  line-height: 1;
  background: rgba(0,0,0,0.08);
  border-radius: 12px;
  padding: 2px 6px;
  margin-top: 4px;
  user-select: none;
}
.reaction-badge--outbound {
  cursor: pointer;
  background: rgba(var(--primary-rgb, 115,103,240), 0.15);
  border: 1px solid rgba(var(--primary-rgb, 115,103,240), 0.3);
}
.reaction-badge--outbound:hover {
  background: rgba(var(--primary-rgb, 115,103,240), 0.25);
}

</style>

<!-- Estilos da Tour (não-scoped para funcionar corretamente) -->
<style lang="scss">
@import '@core/scss/vue/libs/tour.scss';
</style>

<style scoped>
/* Modo escuro */
body.dark-layout .chat-header { background-color: #293046; }
body.dark-layout .chat-footer { background-color: #293046; }
body.dark-layout .chat-body { background-color: #38415f; }

/* T16: Mobile — smaller send/mic button */
@media (max-width: 767px) {
  .btn-send-mic {
    width: 36px;
    height: 36px;
  }

  .btn-send-mic-inner {
    width: 36px;
    height: 36px;
  }

  .input-mensagem::placeholder {
    font-size: 12px;
  }
}

/* T36: Send/mic even smaller at ≤375px */
@media (max-width: 375px) {
  .btn-send-mic {
    width: 34px;
    height: 34px;
  }
  .btn-send-mic-inner {
    width: 34px;
    height: 34px;
  }
}
</style>

<!-- Estilos da Tour (não-scoped para funcionar corretamente) -->
<style lang="scss">
@import '@core/scss/vue/libs/tour.scss';
</style>
